import { useState, useRef, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import Avvvatars from "avvvatars-react";
import Icon1 from "@assets/icons/Icon";
import { UserAvatar } from "@components/ui/UserAvatar";
import ClientAvatar from "@components/chat/ClientAvatar";
import { ChunkyTabs } from "@components/ui/selection/ChunkyTabs";
import ChatPanel from "@components/chat/ChatPanel";
import FloatingChatWidget from "@components/chat/FloatingChatWidget";
import SearchField from "@components/ui/textfields/SearchField";
import TextField from "@components/ui/textfields/TextField";
import DropDown from "@components/ui/dropdown/DropDown";
import AssignDropdownShared from "@components/ui/dropdown/AssignDropdown";
import { getStatusColor, STATUS_CHIP_STYLES } from "@components/ui/dropdown/OrdreStatusDropdown";
import DatePicker from "@components/ui/selection/DatePicker";
import MenuButton from "@components/ui/buttons/MenuButton";
import SideMenuButton from "@navigation/SideMenuButton";
import OrdreActionsMenu from "@components/ui/OrdreActionsMenu";
import EditRentalReservationModal from "@components/modals/EditRentalReservationModal";
import ClientNotesBanner from "@components/ui/ClientNotesBanner";
import MenuInception from "@components/ui/buttons/MenuInception";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import BaseModal from "@components/modals/BaseModal";
import ConfirmationModal from "@components/support/ConfirmationModal";
import CheckBox from "@components/ui/selection/CheckBox";
import OrdreModal from "@components/modals/OrdreModal";
import SecondaryOrdreModal from "@components/modals/SecondaryOrdreModal";
import { AddDocumentModalOrdre, FactureUploadModal, RentalVehicleModal, TrackingPreviewModal } from "@components/modals/OrdreActionModals";
import { Text1 } from "@components/ui/texts/Texts";
import { DocumentPreviewModal } from "@components/ui/DocumentPreviewModal";
import { getOrdreById, useOrdreStore } from "@store/ordreStore";
import { useAtelierStore } from "@store/atelierStore";
import { usePlanningStore } from "@store/planningStore";
import { useDataStore } from "@store/user_org";
import { showNotification } from "context/notification.service";
import { navigateTo } from "context/navigation.service";
import { dossiersApi } from "@services/api/dossiers";
import { clientVehiclesApi } from "@services/api/clientVehicles";
import { ordresApi } from "@services/api/ordres";
import { atelierApi } from "@services/api/atelier";
import firebaseService from "@services/firebase/index";
import { doc, onSnapshot } from "firebase/firestore";
import type {
  Ordre,
  OrdreStatus,
  OrdreIntervention,
  OrdreDocument,
  OrdreTimelineEvent,
  TrackingStep,
  InterventionGroupStatus,
  OrdreFlag,
} from "types/ordre";
import { ORDRE_FLAG_CONFIG, isAPrevoir, getAssignedIds } from "types/ordre";
import type { Dossier } from "types/dossier";
import type { Contact } from "types/contact";
import type { ClientVehicle } from "types/client_vehicle";
import { useTagStore } from "@store/tagStore";
import InlineTagEditor from "@components/ui/tags/InlineTagEditor";
import { TrackingContentV2 } from "@screens/TrackingScreenV2";
import { getTrackingData, type TrackingData } from "@services/api/tracking";
import { vehiclesApi } from "@services/api/vehicles";
import type { Vehicle } from "types/garage/vehicules";
import { interventionsApi, type Intervention } from "@services/api/interventions";
import PaymentRequestModal from "@components/modals/PaymentRequestModal";
import ManualPaymentModal from "@components/modals/ManualPaymentModal";
import EstimateExtractionModal from "@components/modals/EstimateExtractionModal";
import ServiceBoxTransferModal from "@components/modals/ServiceBoxTransferModal";
import ContratLocationModal from "@components/modals/ContratLocationModal";
import VehicleEditModal from "@components/modals/VehicleEditModal";
import ContactFormModal from "@components/modals/ContactFormModal";
import DevisReminderModal from "@components/modals/DevisReminderModal";
import { paymentsApi } from "@services/api/payments";
import type { Payment } from "types/payment";
import { PAYMENT_METHOD_LABELS } from "types/payment";
import { recallCampaignsApi } from "@services/api/recallCampaigns";

import VehicleImage from "@components/ui/VehicleImage";
import { useMessagerieStore } from "@store/messagerieStore";
import { serviceboxProxy, PROXY_BUSY_MESSAGE, type ForfaitChoice } from "@services/serviceboxProxy";
import { Switch } from "@components/ui/selection/Switch";
import { estimateExtractionApi } from "@services/api/estimateExtraction";
import type { EstimateExtraction } from "types/estimateExtraction";

// Module-level flag: once the proxy toast has been shown in this session, don't show again
let _proxyToastShownThisSession = false;

// ─── Constants ─────────────────────────────────────────────

const STATUS_OPTIONS: { value: OrdreStatus; label: string; color: string }[] = [
  { value: "a_preparer", label: "À préparer", color: "#A1A1A1" },
  { value: "devis_initial", label: "Devis initial", color: "#f59e0b" },
  { value: "commande", label: "Commandé", color: "#8b5cf6" },
  { value: "pret", label: "Prêt", color: "#06b6d4" },
  { value: "reception", label: "Réceptionné", color: "#f59e0b" },
  { value: "en_cours", label: "En cours", color: "#3b82f6" },
  { value: "devis_supp", label: "Devis supp.", color: "#f59e0b" },
  { value: "accord", label: "Accord", color: "#10b981" },
  { value: "acompte", label: "Acompte", color: "#eab308" },
  { value: "fin_de_travaux", label: "Fin de travaux", color: "#10b981" },
  { value: "facturation", label: "Facturation", color: "#6366f1" },
  { value: "solde", label: "Solde", color: "#eab308" },
  { value: "solde_paye", label: "Solde payé", color: "#10b981" },
  { value: "disponible", label: "Disponible", color: "#22c55e" },
  { value: "restitue", label: "Restitué", color: "#10b981" },
  { value: "arrete", label: "Arrêté", color: "#B91C1C" },
];

// Auto-mapping: when ordre status changes, tracking_step follows
const STATUS_TO_TRACKING: Partial<Record<OrdreStatus, TrackingStep>> = {
  a_preparer: "en_attente_depot",
  devis_initial: "devis_pret",
  commande: "en_attente_depot",
  pret: "en_attente_depot",
  reception: "vehicule_depose",
  en_cours: "reparations_en_cours",
  devis_supp: "devis_supplementaire",
  accord: "reparations_en_cours",
  acompte: "acompte_manquant",
  fin_de_travaux: "travaux_termines",
  facturation: "facture_disponible",
  solde: "solde_en_attente",
  solde_paye: "paiement_valide",
  disponible: "vehicule_disponible",
  restitue: "vehicule_restitue",
};

const TRACKING_STEP_OPTIONS: { value: TrackingStep; label: string }[] = [
  { value: "carte_grise_manquante", label: "Carte grise manquante" },
  { value: "acompte_manquant", label: "Acompte en attente" },
  { value: "en_attente_depot", label: "En attente du véhicule" },
  { value: "vehicule_depose", label: "Véhicule déposé" },
  { value: "devis_pret", label: "Devis prêt" },
  { value: "reparations_en_cours", label: "Réparations en cours" },
  { value: "solde_en_attente", label: "Paiement final en attente" },
  { value: "paiement_valide", label: "Paiement validé" },
  { value: "travaux_termines", label: "Travaux terminés" },
  { value: "facture_disponible", label: "Facture disponible" },
  { value: "vehicule_disponible", label: "Véhicule disponible" },
  { value: "vehicule_restitue", label: "Véhicule restitué" },
];

/** Compute effective tracking step from status + flags — mirrors backend compute_tracking_step. */
function computeTrackingStep(ordre: Ordre): TrackingStep {
  const base = ordre.tracking_step || "en_attente_depot";
  const status = ordre.status;

  // Terminal statuses always win
  if (status === "restitue") return "vehicule_restitue";
  if (status === "disponible") return "vehicule_disponible";

  // New dedicated statuses map directly
  if (status === "solde_paye") return "paiement_valide";
  if (status === "solde") return "solde_en_attente";
  if (status === "acompte") return "acompte_manquant";
  if (status === "devis_initial") return "devis_pret";

  // Flag-based overrides (legacy compat)
  if (ordre.solde_requested && !ordre.solde_paid) return "solde_en_attente";
  if (ordre.acompte_requested && !ordre.acompte_paid) return "acompte_manquant";
  if (ordre.solde_paid) return "paiement_valide";
  if (ordre.devis_requested && !ordre.devis_signed) return "devis_pret";

  // Status-based derivation
  if (status === "facturation") return "facture_disponible";
  if (status === "fin_de_travaux") return "travaux_termines";
  if (status === "en_cours" || status === "accord") return "reparations_en_cours";
  if (status === "devis_supp") {
    if (ordre.devis_signed) return "reparations_en_cours";
    return ordre.devis_requested ? "devis_pret" : "vehicule_depose";
  }

  // Fallback: fix stale stored values
  if (base === "acompte_manquant" && ordre.acompte_paid) return "reparations_en_cours";
  if (base === "paiement_valide" && !ordre.solde_paid) return "reparations_en_cours";
  if (base === "devis_pret" && ordre.devis_signed) return "reparations_en_cours";
  return base;
}


const CONTENT_TABS = [
  { value: "fil", label: "Fil de l'ordre" },
  { value: "contacts", label: "Contacts" },
  { value: "interventions", label: "Interventions" },

  { value: "vehicules", label: "Véhicules" },
  { value: "documents", label: "Documents" },
];

function formatTimestamp(ts?: { seconds: number }): string {
  if (!ts) return "—";
  return new Date(ts.seconds * 1000).toLocaleDateString("fr-FR", {
    day: "2-digit", month: "2-digit",
  }) + " " + new Date(ts.seconds * 1000).toLocaleTimeString("fr-FR", {
    hour: "2-digit", minute: "2-digit",
  });
}

function formatHour(h: number): string {
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return `${hrs}h${mins.toString().padStart(2, "0")}`;
}

// ─── Timeline component ────────────────────────────────────

function formatEventDate(iso: string): { text: string; color?: string } {
  const d = new Date(iso);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const eventDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

  if (eventDay.getTime() === today.getTime()) {
    return { text: `Auj. ${time}`, color: "#10b981" };
  }
  if (eventDay.getTime() === yesterday.getTime()) {
    return { text: `Hier ${time}`, color: "#171717" };
  }
  return { text: d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) + " " + time };
}

const IMAGE_NAME_RE = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i;

function TimelineDocThumbnail({ docName, documents, ordreId, onPreview }: { docName: string; documents: OrdreDocument[]; ordreId: string; onPreview: (doc: any, url: string) => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const docRef = useState(() => documents.find((d) => d.name === docName))[0];

  useEffect(() => {
    if (!docRef) return;
    let cancelled = false;
    ordresApi.getDocumentUrl(ordreId, docRef.id).then((u) => { if (!cancelled) setUrl(u); }).catch(() => {});
    return () => { cancelled = true; };
  }, [docName, ordreId]);

  return (
    <div
      className="w-[80px] h-[80px] rounded-lg overflow-hidden bg-neutral-100 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
      onClick={() => { if (url && docRef) onPreview(docRef, url); }}
    >
      {url ? (
        <img src={url} alt={docName} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Icon1 name="image" size={24} color="#D4D4D4" />
        </div>
      )}
    </div>
  );
}

function TimelineEvent({ event, isLast, documents, ordreId, onPreview, orgMembers }: { event: OrdreTimelineEvent; isLast: boolean; documents?: OrdreDocument[]; ordreId?: string; onPreview?: (doc: any, url: string) => void; orgMembers?: import("types/user").User[] }) {
  const matchedMember = orgMembers?.find((m) => m.infos.display_name === event.user_name || m.infos.full_name === event.user_name);
  const imageNames = (event.type === "documents" && event.document_names)
    ? event.document_names.filter((n) => IMAGE_NAME_RE.test(n))
    : [];
  const fileNames = (event.type === "documents" && event.document_names)
    ? event.document_names.filter((n) => !IMAGE_NAME_RE.test(n))
    : [];

  return (
    <div className="flex flex-row">
      <div className="flex-shrink-0 text-h3 font-medium" style={{ width: 90, paddingTop: 2, textAlign: "right", paddingRight: 12, color: formatEventDate(event.date).color || "#a3a3a3" }}>
        {formatEventDate(event.date).text}
      </div>
      <div className="flex flex-col items-center flex-shrink-0" style={{ width: 20 }}>
        <div className="rounded-full mt-1.5 flex-shrink-0" style={{ width: 10, height: 10, background: "#A3A3A3" }} />
        {!isLast && <div className="w-0.5 bg-neutral-300 flex-1 my-1" />}
      </div>
      <div className="flex flex-col gap-2 ml-3 flex-1 pb-6" style={{ minWidth: 0 }}>
        <div className="flex flex-row gap-2 items-start">
          {event.sender_type === "client" ? (
            <ClientAvatar size={32} />
          ) : (
            <UserAvatar
              src={matchedMember?.customization.photo_url}
              value={matchedMember?.customization.avatar_seed || event.user_name}
              size={32}
              className="w-8 h-8 rounded-full flex-shrink-0"
            />
          )}
          <div className="flex flex-col">
            <span className="text-h2 font-semibold" style={{ lineHeight: "18px" }}>{event.user_name}</span>
            <span className="text-h3 text-neutral-400">{event.action}</span>
          </div>
        </div>
        {event.type === "documents" && imageNames.length > 0 && documents && ordreId && (
          <div className="flex flex-row gap-2 mt-1 flex-wrap">
            {imageNames.map((name, i) => (
              <TimelineDocThumbnail key={i} docName={name} documents={documents} ordreId={ordreId} onPreview={onPreview ?? (() => {})} />
            ))}
          </div>
        )}
        {event.type === "documents" && fileNames.length > 0 && (
          <div className="flex flex-col gap-0.5 mt-1">
            {fileNames.map((name, i) => (
              <div key={i} className="flex flex-row items-center gap-2 cursor-pointer hover:bg-neutral-50 rounded px-1 py-0.5">
                <Icon1 name="file" size={14} color="#A1A1A1" />
                <span className="text-h3 text-neutral-600">{name}</span>
              </div>
            ))}
          </div>
        )}
        {event.type === "photos" && event.photo_urls && (
          <div className="flex flex-row gap-2 mt-1 flex-wrap">
            {event.photo_urls.map((url, i) => (
              <div
                key={i}
                className="w-[80px] h-[80px] rounded-lg overflow-hidden bg-neutral-100 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
                onClick={() => onPreview?.({ name: `Photo ${i + 1}`, gcs_path: url }, url)}
              >
                <img src={url} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        )}
        {event.type === "status_change" && event.status_label && (
          <div className={`flex flex-row items-center gap-2 mt-1 px-2 py-1 rounded-lg w-fit ${event.status_type === "warning" ? "shadow-md bg-amber-50" : ""}`}>
            <span className="text-h3 font-medium" style={{ color: event.status_type === "success" ? "#10b981" : event.status_type === "warning" ? "#f59e0b" : "#6366f1" }}>
              {event.status_label}
            </span>
            <Icon1 name={event.status_type === "success" ? "done" : "horloge"} size={16} color={event.status_type === "success" ? "#10b981" : event.status_type === "warning" ? "#f59e0b" : "#6366f1"} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Fil de l'ordre tab ─────────────────────────────────────

function FilTab({ timeline, search, documents, ordreId, onPreview, orgMembers }: { timeline: OrdreTimelineEvent[]; search: string; documents?: OrdreDocument[]; ordreId?: string; onPreview?: (doc: any, url: string) => void; orgMembers?: import("types/user").User[] }) {
  const filtered = search.trim()
    ? timeline.filter((ev) => {
        const q = search.toLowerCase();
        return ev.user_name.toLowerCase().includes(q) || ev.action.toLowerCase().includes(q) || (ev.document_names || []).some((n) => n.toLowerCase().includes(q)) || (ev.status_label || "").toLowerCase().includes(q);
      })
    : timeline;
  const sorted = [...filtered].sort((a, b) => (a.date < b.date ? 1 : -1));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col">
        {sorted.length === 0 ? (
          <div className="text-h3 text-center text-neutral-400 py-8">{search ? "Aucun résultat" : "Aucun événement"}</div>
        ) : sorted.map((ev, i) => <TimelineEvent key={ev.id} event={ev} isLast={i === sorted.length - 1} documents={documents} ordreId={ordreId} onPreview={onPreview} orgMembers={orgMembers} />)}
      </div>
    </div>
  );
}

// ─── Contacts tab (editable form) ──────────────────────────

function ContactsTab({ contacts: initial, dossierId, ordreId }: { contacts: Contact[]; dossierId?: string; ordreId: string }) {
  const [contacts, setContacts] = useState<Contact[]>(initial);
  const [saving, setSaving] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [addingContact, setAddingContact] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingData, setPendingData] = useState<Partial<Contact> | null>(null);
  const orgData = useDataStore((s) => s.orgData);
  // Auto-push a primary-contact client edit to ServiceBox when this ordre is already transferred.
  const [sbUpdateOrdre, setSbUpdateOrdre] = useState<Ordre | null>(null);

  const saveContacts = async (updated: Contact[]): Promise<Ordre | null> => {
    setSaving(true);
    try {
      if (dossierId) {
        await dossiersApi.update(dossierId, { contacts: updated } as any);
      }
      const enriched = await ordresApi.get(ordreId);
      useOrdreStore.getState().updateOrdre(enriched);
      setContacts(updated);
      return enriched;
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la sauvegarde" });
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handleContactSave = (data: Partial<Contact>) => {
    if (editingContact) {
      // Check if primary contact email/phone changed
      if (editingContact.is_primary && (data.email !== editingContact.email || data.phone !== editingContact.phone)) {
        setPendingData(data);
        setShowConfirm(true);
        return;
      }
      applyContactSave(data);
    } else {
      // Adding new contact
      const newContact: Contact = {
        id: `c-new-${Date.now()}`,
        dossier_id: dossierId || "",
        nom: data.nom || "",
        prenom: data.prenom,
        civilite: data.civilite,
        phone: data.phone,
        email: data.email,
        role: data.role,
        adresse: data.adresse,
        pays: data.pays || "FRANCE",
        is_primary: data.is_primary || false,
        created_at: new Date().toISOString(),
      };
      saveContacts([...contacts, newContact]);
      setAddingContact(false);
    }
  };

  const applyContactSave = async (data: Partial<Contact>) => {
    const target = editingContact!;
    const updated = contacts.map((c) => (c.id === target.id ? { ...c, ...data } : c));
    const clientChanged = (["adresse", "nom", "prenom", "phone", "email", "civilite"] as const).some(
      (k) => data[k] !== undefined && data[k] !== target[k],
    );
    setEditingContact(null);
    const enriched = await saveContacts(updated);
    if (target.is_primary && clientChanged && enriched && orgData?.servicebox?.enabled && enriched.servicebox_rdv_id) {
      setSbUpdateOrdre(enriched);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-row items-center justify-between">
        <span className="text-h3 font-medium" style={{ fontFamily: "Poppins", color: "#525252" }}>Contacts</span>
        <SecondaryButton variant="small" onClick={() => setAddingContact(true)}>
          <span className="flex flex-row items-center gap-1">
            <Icon1 name="plus" size={12} />
            Ajouter
          </span>
        </SecondaryButton>
      </div>
      {contacts.map((c) => {
        const displayName = [c.prenom, c.nom].filter(Boolean).join(" ") || "—";
        return (
          <div key={c.id} className="flex flex-row items-center gap-3 bg-white rounded-lg border border-neutral-200 px-3 py-2">
            <div className="flex-shrink-0 overflow-hidden" style={{ borderRadius: 999 }}>
              <Avvvatars value={displayName} style="shape" size={32} />
            </div>
            <div className="flex-1 min-w-0 flex flex-col">
              <span className="text-h3 font-medium" style={{ fontFamily: "Poppins" }}>
                {displayName}
                {c.is_primary && <span className="ml-2 text-h4 text-indigo-500 font-normal">Principal</span>}
              </span>
              <span className="text-h4 text-neutral-400" style={{ fontFamily: "Poppins" }}>{c.role || "—"} · {c.phone || c.email || "—"}</span>
            </div>
            <button
              onClick={() => setEditingContact(c)}
              className="flex-shrink-0 text-h4 font-medium px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors"
              style={{ color: "#6366f1" }}
            >
              Modifier
            </button>
          </div>
        );
      })}
      {contacts.length === 0 && (
        <span className="text-h3 text-neutral-400 py-4 text-center">Aucun contact</span>
      )}

      {/* Edit/Add modal */}
      {(editingContact || addingContact) && (
        <ContactFormModal
          contact={editingContact}
          onClose={() => { setEditingContact(null); setAddingContact(false); }}
          onSave={handleContactSave}
          saving={saving}
        />
      )}

      {/* Confirmation for primary contact changes */}
      {showConfirm && (
        <ConfirmationModal
          title="Modifier le contact principal"
          message="Ce contact est utilisé pour les notifications WhatsApp et emails. Voulez-vous vraiment modifier ses coordonnées ?"
          confirmLabel="Oui, mettre à jour"
          onConfirm={() => { setShowConfirm(false); if (pendingData) applyContactSave(pendingData); setPendingData(null); }}
          onClose={() => { setShowConfirm(false); setPendingData(null); }}
        />
      )}

      {sbUpdateOrdre && orgData?.servicebox && (
        <ServiceBoxTransferModal
          autoRun
          ordre={sbUpdateOrdre}
          serviceboxConfig={orgData.servicebox}
          onClose={() => setSbUpdateOrdre(null)}
        />
      )}
    </div>
  );
}

// ContactFormModal is now imported from @components/modals/ContactFormModal

// ─── Interventions tab ──────────────────────────────────────

const GROUP_STATUS_LABELS: Record<string, string> = {
  draft: "Brouillon",
  devis_sent: "Devis envoyé",
  devis_signed: "Devis signé",
  ready: "Prêt",
};

const GROUP_STATUS_COLORS: Record<string, string> = {
  draft: "bg-neutral-100 text-neutral-500",
  devis_sent: "bg-blue-100 text-blue-700",
  devis_signed: "bg-amber-100 text-amber-700",
  ready: "bg-green-100 text-green-700",
};

const INTERVENTION_TYPE_LABELS: Record<string, string> = {
  entretien: "Entretien / Révision", reparation: "Réparation / Panne", diagnostic: "Diagnostic",
  controle_technique: "Contrôle technique", pneumatique: "Pneus", carrosserie: "Carrosserie / Vitrage",
};
const INTERVENTION_TYPE_OPTIONS = Object.entries(INTERVENTION_TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }));

function InterventionPickerModal({ onClose, onAdd, configuredInterventions, onCatalogUpdated, group = "initial" }: {
  onClose: () => void;
  onAdd: (inter: OrdreIntervention) => void;
  configuredInterventions: Intervention[];
  onCatalogUpdated: (items: Intervention[]) => void;
  group?: string;
}) {
  const workers = useAtelierStore((s) => s.workers);
  const workerOptions = [
    { value: "", label: "Non assigné" },
    ...workers.map((w) => ({ value: w.id, label: w.name })),
  ];

  const [mode, setMode] = useState<"catalog" | "new">("catalog");
  const [selectedCatalog, setSelectedCatalog] = useState<Intervention | null>(null);
  const [search, setSearch] = useState("");
  const [label, setLabel] = useState("");
  const [estimatedH, setEstimatedH] = useState("");
  const [assignedTo, setAssignedTo] = useState("");

  const resetForm = () => {
    setSelectedCatalog(null); setLabel(""); setEstimatedH(""); setAssignedTo(""); setSearch("");
  };

  const handleSelectCatalog = (item: Intervention) => {
    setSelectedCatalog(item);
    const h = +(item.estimated_minutes / 60).toFixed(2);
    setLabel(item.name);
    setEstimatedH(String(h));
    setAssignedTo("");
  };

  const canAdd = label.trim() !== "" && (mode === "catalog" ? selectedCatalog !== null : true);

  // "Nouvelle" creates an intervention specific to THIS ordre only — it is NOT
  // saved to the catalog (no interventionsApi.create, no category needed).
  const handleAdd = () => {
    if (!canAdd) return;
    onAdd({
      id: `i-${Date.now()}`,
      label,
      estimated_hours: parseFloat(estimatedH) || 0,
      assigned_to: assignedTo,
      group,
    });
    onClose();
  };

  return (
    <BaseModal title="Ajouter une intervention" startIcon={<Icon1 name="plus" size={20} />} onClose={onClose} width="max-w-lg">
      <div className="p-4 flex flex-col gap-4">
        <ChunkyTabs
          activeTab={mode}
          tabs={[{ value: "catalog", label: "Depuis le catalogue" }, { value: "new", label: "Nouvelle" }]}
          setActiveTab={(t) => { setMode(t as "catalog" | "new"); resetForm(); }}
        />

        {mode === "catalog" && (
          <div className="flex flex-col gap-3">
            <TextField placeholder="Rechercher une intervention…" value={search} onChange={setSearch} />
            <div className="overflow-y-auto rounded-lg border border-neutral-200" style={{ maxHeight: 220 }}>
              {Object.entries(INTERVENTION_TYPE_LABELS).map(([cat, catLabel]) => {
                const q = search.trim().toLowerCase();
                const items = configuredInterventions
                  .filter((i) => i.type === cat && i.active && (!q || i.name.toLowerCase().includes(q)))
                  .sort((a, b) => a.name.localeCompare(b.name, "fr"));
                if (!items.length) return null;
                return (
                  <div key={cat}>
                    <div className="px-3 py-1 text-h5 font-semibold text-neutral-400 bg-neutral-50 sticky top-0">{catLabel}</div>
                    {items.map((item) => {
                      const h = +(item.estimated_minutes / 60).toFixed(2);
                      const isSelected = selectedCatalog?.id === item.id;
                      return (
                        <button
                          key={item.id}
                          onClick={() => handleSelectCatalog(item)}
                          className={`flex flex-row items-center justify-between w-full px-3 py-2 text-left transition-colors ${isSelected ? "bg-indigo-50" : "hover:bg-neutral-50"}`}
                        >
                          <span className={`text-h3 ${isSelected ? "text-indigo-700 font-medium" : "text-neutral-600"}`}>{item.name}</span>
                          <span className="text-h4 text-neutral-400 ml-2 flex-shrink-0">{h}h</span>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
              {(() => {
                const q = search.trim().toLowerCase();
                const matches = configuredInterventions.filter((i) => i.active && (!q || i.name.toLowerCase().includes(q)));
                if (matches.length > 0) return null;
                return (
                  <div className="px-3 py-6 text-h3 text-neutral-400 text-center">
                    {q ? "Aucune intervention trouvée" : "Aucune intervention dans le catalogue"}
                  </div>
                );
              })()}
            </div>
            {selectedCatalog && (
              <div className="flex flex-col gap-3 pt-2 border-t border-neutral-100">
                <TextField label="Libellé" value={label} onChange={setLabel} />
                <div className="flex flex-row gap-2">
                  <div className="flex-1">
                    <TextField label="Heures estimées" type="number" value={estimatedH} onChange={setEstimatedH} />
                  </div>
                  <div className="flex-1">
                    <DropDown label="Assigné à" options={workerOptions} value={assignedTo} onSelect={(v) => setAssignedTo(String(v))} />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {mode === "new" && (
          <div className="flex flex-col gap-3">
            <TextField label="Libellé" value={label} onChange={setLabel} />
            <div className="flex flex-row gap-2">
              <div className="flex-1">
                <TextField label="Durée estimée (h)" type="number" value={estimatedH} onChange={setEstimatedH} />
              </div>
              <div className="flex-1">
                <DropDown label="Assigné à" options={workerOptions} value={assignedTo} onSelect={(v) => setAssignedTo(String(v))} />
              </div>
            </div>
            <span className="text-h4 text-neutral-400">Intervention spécifique à cet ordre (non ajoutée au catalogue).</span>
          </div>
        )}
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>Annuler</SecondaryButton>
        <Button variant="small" label="Ajouter" onClick={handleAdd} disabled={!canAdd} />
      </div>
    </BaseModal>
  );
}

// ─── Transfer to Atelier Modal ─────────────────────────────

const ATELIER_SPECIALTIES: { value: string; label: string }[] = [
  { value: "mecanique", label: "Mécanique" },
  { value: "carrosserie", label: "Carrosserie" },
  { value: "peinture", label: "Peinture" },
  { value: "diagnostique", label: "Diagnostique" },
  { value: "service_rapide", label: "Service rapide" },
  { value: "campagnes", label: "Campagnes" },
  { value: "autre", label: "Autre" },
];

function TransferAtelierModal({ interventions, ordreId, group, onClose, onDone }: {
  interventions: OrdreIntervention[];
  ordreId: string;
  group: string;
  onClose: () => void;
  onDone: () => void;
}) {
  // Each intervention gets assigned a specialty — default to "mecanique"
  const [assignments, setAssignments] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    interventions.forEach((i) => { init[i.id] = "mecanique"; });
    return init;
  });
  const [saving, setSaving] = useState(false);

  const setSpecialty = (interventionId: string, specialty: string) => {
    setAssignments((prev) => ({ ...prev, [interventionId]: specialty }));
  };

  // Group interventions by assigned specialty for preview
  const groupedBySpecialty = useMemo(() => {
    const groups: Record<string, OrdreIntervention[]> = {};
    interventions.forEach((inter) => {
      const sp = assignments[inter.id] || "mecanique";
      if (!groups[sp]) groups[sp] = [];
      groups[sp].push(inter);
    });
    return groups;
  }, [interventions, assignments]);

  const handleTransfer = async () => {
    setSaving(true);
    try {
      const charges = Object.entries(groupedBySpecialty).map(([specialty, inters]) => ({
        specialty,
        intervention_ids: inters.map((i) => i.id),
        hours: inters.reduce((s, i) => s + (i.estimated_hours || 0), 0),
      }));
      await ordresApi.transferToAtelier(ordreId, group, charges);
      showNotification({ type: "success", message: "Interventions transférées à l'atelier" });
      onDone();
      onClose();
    } catch (e: any) {
      showNotification({ type: "error", message: e?.response?.data?.detail || "Erreur lors du transfert" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <BaseModal title="Transférer à l'atelier" startIcon={<Icon1 name="side-atelier" size={20} />} onClose={onClose} width="max-w-lg">
      <div className="p-4 flex flex-col gap-4">
        <span className="text-h4 text-neutral-400">
          Assignez chaque intervention à une spécialité atelier. Une charge sera créée par spécialité.
        </span>

        {/* Intervention list with specialty selector */}
        <div className="flex flex-col gap-2">
          {interventions.map((inter) => (
            <div key={inter.id} className="flex flex-row items-center gap-3 bg-neutral-50 rounded-lg px-3 py-2">
              <div className="flex flex-col flex-1 min-w-0">
                <div className="flex flex-row items-center gap-2">
                  <span className="text-h3 font-medium">{inter.label || "Intervention"}</span>
                  {inter.forfait_code && (
                    <span
                      className="text-h4 font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-200"
                      title="Code forfait ServiceBox (transféré comme Forfait Local)"
                    >
                      Forfait {inter.forfait_code}
                    </span>
                  )}
                </div>
                <span className="text-h4 text-neutral-400">{inter.estimated_hours}h</span>
              </div>
              <div style={{ minWidth: 160 }}>
                <DropDown
                  variant="small"
                  options={ATELIER_SPECIALTIES}
                  value={assignments[inter.id] || "mecanique"}
                  onSelect={(v) => setSpecialty(inter.id, String(v))}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Summary by specialty */}
        <div className="border-t border-neutral-100 pt-3 flex flex-col gap-1">
          <span className="text-h4 font-medium text-neutral-500 mb-1">Charges créées</span>
          {Object.entries(groupedBySpecialty).map(([sp, inters]) => {
            const style = SPECIALTY_STYLE[sp] || SPECIALTY_STYLE.autre;
            const totalH = inters.reduce((s, i) => s + (i.estimated_hours || 0), 0);
            return (
              <div key={sp} className="flex flex-row items-center justify-between px-3 py-1.5 rounded-md" style={{ backgroundColor: style.bg }}>
                <span className="text-h3 font-medium" style={{ color: style.color }}>
                  {ATELIER_SPECIALTIES.find((s) => s.value === sp)?.label || sp}
                </span>
                <span className="text-h4 font-semibold" style={{ color: style.color }}>
                  {inters.length} intervention{inters.length > 1 ? "s" : ""} — {totalH}h
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
        <SecondaryButton variant="small" onClick={onClose}>Annuler</SecondaryButton>
        <Button variant="small" label={saving ? "Transfert..." : "Transférer"} onClick={handleTransfer} disabled={saving || interventions.length === 0} />
      </div>
    </BaseModal>
  );
}

function InterventionGroupSection({
  title,
  group,
  interventions,
  groupStatus,
  configuredInterventions,
  onAddIntervention,
  onRemoveIntervention,
  onToggleStatus,
  hasPlacedJobs,
  onCatalogUpdated,
  ordreId,
  onRefresh,
}: {
  title: string;
  group: string;
  interventions: OrdreIntervention[];
  groupStatus?: InterventionGroupStatus;
  configuredInterventions: Intervention[];
  onAddIntervention: (group: string, inter: OrdreIntervention) => void;
  onRemoveIntervention: (id: string) => void;
  onToggleStatus: (id: string) => void;
  hasPlacedJobs: boolean;
  onCatalogUpdated: (items: Intervention[]) => void;
  ordreId: string;
  onRefresh: () => void;
}) {
  const [collapsed, setCollapsed] = useState(false);
  const [showPickerModal, setShowPickerModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const status = groupStatus?.status || "draft";

  // "À prévoir" lines are saved to the dossier for a later visit — they don't count
  // toward this RDV's estimated workload.
  const totalEstimated = interventions.filter((i) => !isAPrevoir(i)).reduce((s, i) => s + (i.estimated_hours || 0), 0);
  const aPrevoirCount = interventions.filter(isAPrevoir).length;

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      {/* Section header */}
      <div
        className="flex flex-row items-center justify-between px-4 py-3 bg-neutral-50 cursor-pointer hover:bg-neutral-100"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex flex-row items-center gap-3">
          <Icon1 name={collapsed ? "right" : "down"} size={12} color="#525252" />
          <span className="text-h3 font-semibold">{title}</span>
          <span className="text-h4 text-neutral-400">({interventions.length})</span>
        </div>
        <span className={`text-h5 font-medium px-2 py-0.5 rounded-full ${GROUP_STATUS_COLORS[status]}`}>
          {GROUP_STATUS_LABELS[status]}
        </span>
      </div>

      {!collapsed && (
        <div className="p-3 flex flex-col gap-2">
          {/* Intervention rows */}
          {interventions.map((inter) => {
            const aPrevoir = isAPrevoir(inter);
            return (
            <div
              key={inter.id}
              className={`flex flex-row items-center justify-between rounded-lg border px-3 py-2 ${
                aPrevoir ? "bg-amber-50/50 border-amber-200" : "bg-white border-neutral-200"
              }`}
            >
              <div className={`flex flex-col flex-1 min-w-0 ${aPrevoir ? "opacity-70" : ""}`}>
                <div className="flex flex-row items-center gap-2 flex-wrap">
                  <span className={`text-h3 font-medium ${aPrevoir ? "line-through decoration-amber-400/60" : ""}`}>
                    {inter.label || "Nouvelle intervention"}
                  </span>
                  {aPrevoir && (
                    <span
                      className="text-h5 font-medium px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200"
                      title="Enregistré sur le dossier — non facturé sur ce RDV, reproposé à la prochaine prise de RDV"
                    >
                      À prévoir
                    </span>
                  )}
                  {inter.forfait_code && (
                    <span
                      className="text-h4 font-medium px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 border border-blue-200"
                      title="Code forfait ServiceBox (transféré comme Forfait Local)"
                    >
                      Forfait {inter.forfait_code}
                    </span>
                  )}
                </div>
                <span className="text-h4 text-neutral-400">
                  {inter.estimated_hours}h estimée
                </span>
              </div>
              <div className="flex flex-row items-center gap-1 flex-shrink-0">
                <button
                  className={`text-h5 font-medium px-2 py-1 rounded border transition-colors ${
                    aPrevoir
                      ? "border-neutral-200 text-neutral-600 hover:bg-neutral-100"
                      : "border-amber-200 text-amber-700 hover:bg-amber-50"
                  }`}
                  onClick={() => onToggleStatus(inter.id)}
                  title={aPrevoir ? "Réintégrer cette intervention dans le RDV" : "Basculer en travail à prévoir (enregistré sur le dossier)"}
                >
                  {aPrevoir ? "Inclure" : "À prévoir"}
                </button>
                {hasPlacedJobs ? (
                  <span
                    className="text-h4 text-neutral-200 px-1 cursor-not-allowed"
                    title="Impossible de supprimer : travail déjà placé en atelier"
                  >
                    <Icon1 name="close" size={12} />
                  </span>
                ) : (
                  <button
                    className="p-1 rounded hover:bg-neutral-100 transition-colors"
                    onClick={() => onRemoveIntervention(inter.id)}
                    title="Retirer"
                  >
                    <Icon1 name="close" size={14} color="#A1A1A1" />
                  </button>
                )}
              </div>
            </div>
            );
          })}

          {/* Totals row */}
          {interventions.length > 1 && (
            <div className="flex flex-row items-center justify-between px-3 pt-1">
              <span className="text-h4 font-medium text-neutral-400">
                Total{aPrevoirCount > 0 ? ` (hors ${aPrevoirCount} à prévoir)` : ""}
              </span>
              <span className="text-h4 font-semibold text-neutral-700">{totalEstimated}h</span>
            </div>
          )}

          {/* Empty state */}
          {interventions.length === 0 && (
            <div className="flex flex-col items-center py-6 gap-1">
              <span className="text-h3 text-neutral-400">Aucune intervention</span>
              <span className="text-h4 text-neutral-300">Utilisez le bouton ci-dessous pour ajouter depuis le catalogue ou créer une intervention personnalisée</span>
            </div>
          )}

          {/* Add intervention + actions */}
          <div className="flex flex-row items-center gap-3 mt-1 pt-3 border-t border-neutral-100">
            <SecondaryButton
              variant="small"
              startIcon={<Icon1 name="plus" size={14} />}
              label="Ajouter"
              onClick={() => setShowPickerModal(true)}
            />

            <div className="flex flex-row items-center gap-2 ml-auto">
              {(status === "draft" || status === "devis_signed") && interventions.length - aPrevoirCount > 0 && (
                <Button
                  label="Transférer à l'atelier"
                  onClick={() => setShowTransferModal(true)}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {showPickerModal && (
        <InterventionPickerModal
          group={group}
          configuredInterventions={configuredInterventions}
          onCatalogUpdated={onCatalogUpdated}
          onClose={() => setShowPickerModal(false)}
          onAdd={(inter) => onAddIntervention(group, inter)}
        />
      )}

      {showTransferModal && (
        <TransferAtelierModal
          interventions={interventions.filter((i) => !isAPrevoir(i))}
          ordreId={ordreId}
          group={group}
          onClose={() => setShowTransferModal(false)}
          onDone={() => {
            onRefresh();
            const s = useAtelierStore.getState();
            if (s._fetchedOrgId) s.fetchData(s._fetchedOrgId, true);
          }}
        />
      )}
    </div>
  );
}

function InterventionsTab({
  interventions: initial,
  configuredInterventions,
  interventionGroups,
  ordreId,
  atelierCharges,
  onRefresh,
  onCatalogUpdated,
}: {
  interventions: OrdreIntervention[];
  configuredInterventions: Intervention[];
  interventionGroups?: Ordre["intervention_groups"];
  ordreId: string;
  atelierCharges: AtelierChargeRow[];
  onRefresh: () => void;
  onCatalogUpdated: (items: Intervention[]) => void;
}) {
  const [interventions, setInterventions] = useState(initial);
  // Compute existing additional group keys from intervention_groups or interventions
  const existingAdditionalKeys = useMemo(() => {
    const keys = new Set<string>();
    if (interventionGroups) {
      Object.keys(interventionGroups).filter((k) => k.startsWith("additional_")).forEach((k) => keys.add(k));
    }
    initial.forEach((i) => { if (i.group?.startsWith("additional_")) keys.add(i.group); });
    // Legacy compat: treat "additional" as "additional_0"
    if (interventionGroups?.additional || initial.some((i) => i.group === "additional")) {
      keys.add("additional_0");
    }
    return Array.from(keys).sort();
  }, []);
  const [additionalKeys, setAdditionalKeys] = useState<string[]>(existingAdditionalKeys);

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);
  const pendingInterventions = useRef<OrdreIntervention[] | null>(null);

  const doSave = async (toSave: OrdreIntervention[]) => {
    try {
      const updated = await ordresApi.update(ordreId, { interventions: toSave } as any);
      useOrdreStore.getState().updateOrdre(updated);
      pendingInterventions.current = null;
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la sauvegarde des interventions" });
    }
  };

  // Auto-save interventions when they change (debounced)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    pendingInterventions.current = interventions;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => doSave(interventions), 500);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [interventions, ordreId]);

  // Flush pending save on unmount (e.g. tab switch)
  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      if (pendingInterventions.current) doSave(pendingInterventions.current);
    };
  }, [ordreId]);

  const initialInterventions = interventions.filter((i) => (i.group || "initial") === "initial");

  // Helper: get interventions for a given additional group key
  // Legacy compat: "additional_0" matches both "additional_0" and "additional"
  const getAdditionalInterventions = (key: string) =>
    interventions.filter((i) =>
      i.group === key || (key === "additional_0" && i.group === "additional"),
    );

  // Helper: get group status, with legacy compat
  const getGroupStatus = (key: string) => {
    if (!interventionGroups) return undefined;
    return interventionGroups[key] || (key === "additional_0" ? interventionGroups["additional"] : undefined);
  };

  const addIntervention = (_group: string, inter: OrdreIntervention) => {
    setInterventions((prev) => [...prev, inter]);
  };

  // Flip a line between "inclus" and "a_prevoir". The backend stamps the VIN when a line
  // becomes à-prévoir so it can be re-proposed only for the same vehicle.
  const toggleStatus = (id: string) => {
    setInterventions((prev) => {
      const updated = prev.map((i) =>
        i.id === id ? { ...i, status: (isAPrevoir(i) ? "inclus" : "a_prevoir") as OrdreIntervention["status"] } : i,
      );
      // Keep atelier charge hours in sync with the "inclus" workload of the toggled line's group.
      const toggled = prev.find((i) => i.id === id);
      const group = toggled?.group || "initial";
      const groupInclus = updated.filter((i) => {
        const g = i.group || "initial";
        return (g === group || (group === "additional_0" && g === "additional")) && !isAPrevoir(i);
      });
      const unplacedCharge = atelierCharges.find((c) => !c.is_placed);
      if (unplacedCharge) {
        const newHours = groupInclus.reduce((s, i) => s + (i.estimated_hours || 0), 0);
        atelierApi.updateCharge(unplacedCharge.id, { hours: newHours })
          .then(() => { const s = useAtelierStore.getState(); if (s._fetchedOrgId) s.fetchData(s._fetchedOrgId, true); })
          .catch((e) => console.error("Error updating charge:", e));
      }
      return updated;
    });
  };

  const removeIntervention = (id: string) => {
    const removedIntervention = interventions.find((i) => i.id === id);
    const group = removedIntervention?.group || "initial";

    setInterventions((prev) => {
      const updated = prev.filter((i) => i.id !== id);

      // Update/remove atelier charges and jobs when interventions change
      const remainingGroupInterventions = updated.filter((i) => {
        const g = i.group || "initial";
        return g === group || (group === "additional_0" && g === "additional");
      });
      const refreshAtelier = () => {
        const s = useAtelierStore.getState();
        if (s._fetchedOrgId) s.fetchData(s._fetchedOrgId, true);
      };
      const unplacedCharge = atelierCharges.find((c) => !c.is_placed);
      const placedJobs = atelierCharges.filter((c) => c.is_placed);

      if (remainingGroupInterventions.length === 0) {
        // No interventions left — delete all charges and jobs for this ordre
        if (unplacedCharge) {
          atelierApi.deleteCharge(unplacedCharge.id).then(refreshAtelier).catch((e) => console.error("Error deleting charge:", e));
        }
        for (const job of placedJobs) {
          atelierApi.deleteJob(job.id).then(refreshAtelier).catch((e) => console.error("Error deleting job:", e));
        }
      } else {
        const newHours = remainingGroupInterventions.filter((i) => !isAPrevoir(i)).reduce((s, i) => s + (i.estimated_hours || 0), 0);
        // Update hours on unplaced charge
        if (unplacedCharge) {
          atelierApi.updateCharge(unplacedCharge.id, { hours: newHours }).then(refreshAtelier).catch((e) => console.error("Error updating charge:", e));
        }
      }

      return updated;
    });
  };

  const handleAddAdditionalGroup = async () => {
    // Find next index
    const existingIndices = additionalKeys.map((k) => parseInt(k.replace("additional_", ""), 10));
    const nextIndex = existingIndices.length > 0 ? Math.max(...existingIndices) + 1 : 0;
    const newKey = `additional_${nextIndex}`;
    setAdditionalKeys((prev) => [...prev, newKey]);
    // Initialize group on the ordre
    try {
      const ig = { ...(interventionGroups || {}), [newKey]: { status: "draft" as const } };
      await ordresApi.update(ordreId, { intervention_groups: ig } as any);
    } catch {
      // Non-blocking
    }
  };

  const hasPlacedJobs = atelierCharges.some((c) => c.is_placed);

  // Only lock deletion for groups that are "ready" (have atelier charges) AND have placed jobs
  const isGroupLocked = (group: string) => {
    const st = group === "initial"
      ? interventionGroups?.initial?.status
      : getGroupStatus(group)?.status;
    return st === "ready" && hasPlacedJobs;
  };

  return (
    <div className="flex flex-col gap-4">
      <InterventionGroupSection
        title="Interventions initiales"
        group="initial"
        interventions={initialInterventions}
        groupStatus={interventionGroups?.initial}
        configuredInterventions={configuredInterventions}
        onAddIntervention={addIntervention}
        onRemoveIntervention={removeIntervention}
        onToggleStatus={toggleStatus}
        hasPlacedJobs={isGroupLocked("initial")}
        onCatalogUpdated={onCatalogUpdated}
        ordreId={ordreId}
        onRefresh={onRefresh}
      />

      {additionalKeys.map((key, idx) => (
        <InterventionGroupSection
          key={key}
          title={`Interventions supplémentaires${additionalKeys.length > 1 ? ` #${idx + 1}` : ""}`}
          group={key}
          interventions={getAdditionalInterventions(key)}
          groupStatus={getGroupStatus(key)}
          configuredInterventions={configuredInterventions}
          onAddIntervention={addIntervention}
          onRemoveIntervention={removeIntervention}
          onToggleStatus={toggleStatus}
          hasPlacedJobs={isGroupLocked(key)}
          onCatalogUpdated={onCatalogUpdated}
          ordreId={ordreId}
          onRefresh={onRefresh}
        />
      ))}

      <button
        className="flex flex-row items-center gap-2 text-h3 font-medium text-indigo-600 border border-indigo-200 rounded-lg px-4 py-2 w-fit hover:bg-indigo-50"
        onClick={handleAddAdditionalGroup}
      >
        <Icon1 name="plus" size={14} color="#4F46E5" />
        {additionalKeys.length === 0 ? "Ajouter des interventions supplémentaires" : "Ajouter un autre devis supplémentaire"}
      </button>

      <AtelierPlanningSection charges={atelierCharges} onDelete={async (c) => {
        try {
          if (c.is_placed) {
            await atelierApi.deleteJob(c.id);
          } else {
            await atelierApi.deleteCharge(c.id);
          }
          const s = useAtelierStore.getState();
          if (s._fetchedOrgId) await s.fetchData(s._fetchedOrgId, true);

          // If no charges/jobs remain for this ordre, revert any "ready" groups
          // back to "devis_signed" so the user can transfer again.
          const refreshed = useAtelierStore.getState();
          const stillHasWork =
            refreshed.charges.some((ch) => ch.ordre_id === ordreId) ||
            refreshed.jobs.some((j) => j.ordre_id === ordreId);
          if (!stillHasWork && interventionGroups) {
            const ig: Record<string, any> = { ...interventionGroups };
            let changed = false;
            for (const key of Object.keys(ig)) {
              if (ig[key]?.status === "ready") {
                ig[key] = { ...ig[key], status: "devis_signed" };
                changed = true;
              }
            }
            if (changed) {
              await ordresApi.update(ordreId, { intervention_groups: ig } as any);
              onRefresh();
            }
          }
        } catch (e) {
          console.error("Error deleting atelier item:", e);
          showNotification({ type: "error", message: "Erreur lors de la suppression" });
        }
      }} />

    </div>
  );
}

// ─── Atelier planning section (inside Interventions tab) ────

type AtelierChargeRow = {
  id: string;
  worker_name: string;
  specialty: string;
  date: string;
  start_hour: number;
  duration_hours: number;
  is_placed: boolean;
};

const SPECIALTY_STYLE: Record<string, { bg: string; color: string; label: string }> = {
  mecanique:      { bg: "#DBEAFE", color: "#1E40AF", label: "Méca" },
  carrosserie:    { bg: "#FEF3C7", color: "#92400E", label: "Carross." },
  peinture:       { bg: "#FEE2E2", color: "#DC2626", label: "Peinture" },
  diagnostique:   { bg: "#D1FAE5", color: "#059669", label: "Diag." },
  service_rapide: { bg: "#EDE9FE", color: "#6F61FF", label: "Serv. rapide" },
  campagnes:      { bg: "#FFEDD5", color: "#EA580C", label: "Campagnes" },
  autre:          { bg: "#F3F4F6", color: "#374151", label: "Autre" },
};

function AtelierPlanningSection({ charges, onDelete }: { charges: AtelierChargeRow[]; onDelete: (charge: AtelierChargeRow) => void }) {
  const [collapsed, setCollapsed] = useState(false);
  const placed   = charges.filter((c) => c.is_placed);
  const unplaced = charges.filter((c) => !c.is_placed);

  const statusBadge = charges.length === 0 ? null : unplaced.length > 0
    ? <span className="text-h5 font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{unplaced.length} à planifier</span>
    : <span className="text-h5 font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Planifié</span>;

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <div
        className="flex flex-row items-center justify-between px-4 py-3 bg-neutral-50 cursor-pointer hover:bg-neutral-100"
        onClick={() => setCollapsed(!collapsed)}
      >
        <div className="flex flex-row items-center gap-3">
          <Icon1 name={collapsed ? "right" : "down"} size={12} color="#525252" />
          <span className="text-h3 font-semibold">Planification atelier</span>
          <span className="text-h4 text-neutral-400">({charges.length})</span>
          {statusBadge}
        </div>
      </div>

      {!collapsed && (
        <div className="divide-y divide-neutral-100">
          {charges.length === 0 && (
            <div className="px-4 py-6 text-center text-h3 text-neutral-400">Aucune charge atelier</div>
          )}

          {placed.map((c) => {
            const sp = SPECIALTY_STYLE[c.specialty] || SPECIALTY_STYLE.autre;
            return (
              <div key={c.id} className="flex flex-row items-center gap-3 px-4 py-2.5">
                <span className="text-h4 font-medium px-2.5 py-0.5 rounded-full" style={{ background: sp.bg, color: sp.color }}>
                  {c.worker_name}
                </span>
                <span className="text-h5 font-medium px-2 py-0.5 rounded-full" style={{ background: sp.bg, color: sp.color }}>
                  {sp.label}
                </span>
                <span className="text-h3 text-neutral-600">{c.date.split("-").reverse().join("/")}</span>
                <span className="text-h3 text-neutral-500">{formatHour(c.start_hour)} — {formatHour(c.start_hour + c.duration_hours)}</span>
                <span className="text-h4 text-neutral-400 ml-auto">{c.duration_hours}h</span>
                <button className="ml-2 text-neutral-400 hover:text-red-500" onClick={() => onDelete(c)} title="Supprimer">
                  <Icon1 name="close" size={14} />
                </button>
              </div>
            );
          })}

          {unplaced.map((c) => {
            const sp = SPECIALTY_STYLE[c.specialty] || SPECIALTY_STYLE.autre;
            return (
              <div key={c.id} className="flex flex-row items-center gap-3 px-4 py-2.5">
                <span className="text-h4 font-medium px-2.5 py-0.5 rounded-full border border-amber-200 bg-amber-50 text-amber-700">
                  À planifier
                </span>
                <span className="text-h5 font-medium px-2 py-0.5 rounded-full" style={{ background: sp.bg, color: sp.color }}>
                  {sp.label}
                </span>
                <span className="text-h4 text-neutral-400 ml-auto">{c.duration_hours}h</span>
                <button className="ml-2 text-neutral-400 hover:text-red-500" onClick={() => onDelete(c)} title="Supprimer">
                  <Icon1 name="close" size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Véhicules tab (selectable list from dossier, aligned with OrdreModal) ──

function VehiculesTab({
  dossierVehicles,
  selectedVehicleId,
  onSelectVehicle,
  onSaveVehicle,
  parentDossier,
  locked = false,
}: {
  dossierVehicles: ClientVehicle[];
  selectedVehicleId?: string;
  onSelectVehicle: (vehicleId: string) => void;
  onSaveVehicle: (vehicle: ClientVehicle, data: Partial<ClientVehicle>) => void;
  parentDossier?: Dossier | null;
  locked?: boolean;
}) {
  const [editingVehicle, setEditingVehicle] = useState<ClientVehicle | null>(null);

  return (
    <div className="flex flex-col gap-2">
      <span className="text-h3 font-semibold text-neutral-500">Véhicules du dossier</span>
      {locked && (
        <div className="flex flex-row items-center gap-2 rounded-xl bg-neutral-50 border border-neutral-200 px-4 py-2 text-h4 text-neutral-500">
          <Icon1 name="lock" size={14} />
          <span>Ordre transféré dans ServiceBox : le véhicule assigné ne peut plus être changé.</span>
        </div>
      )}
      {dossierVehicles.map((v) => {
        const isSelected = v.id === selectedVehicleId;
        return (
          <div
            key={v.id}
            className="flex flex-row items-center gap-3 rounded-xl border px-4 py-3 transition-colors"
            style={{
              borderColor: isSelected ? "#5C6BFF" : "#E5E5E5",
              background: isSelected ? "#F5F6FF" : "#FFFFFF",
            }}
          >
            <button
              className={`flex flex-row items-center gap-3 flex-1 min-w-0 text-left${locked ? " cursor-default" : ""}`}
              onClick={locked ? undefined : () => onSelectVehicle(v.id)}
              disabled={locked}
            >
              {/* Radio */}
              <div
                className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0"
                style={{
                  borderColor: isSelected ? "#5C6BFF" : "#D4D4D4",
                  background: isSelected ? "#5C6BFF" : "transparent",
                }}
              >
                {isSelected && (
                  <svg width={10} height={8} viewBox="0 0 10 8" fill="none">
                    <path d="M1 4l3 3 5-6" stroke="#FFFFFF" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>

              {/* Vehicle thumbnail */}
              <div className="w-16 h-10 rounded-lg bg-neutral-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                <VehicleImage marque={v.marque} modele={v.modele} coloris={v.coloris} mec={v.mec} alt={v.plate} className="w-full h-full" iconSize={16} />
              </div>

              {/* Vehicle info */}
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-h3 font-medium truncate">
                  {v.marque} {v.modele}
                </span>
                <span className="text-h4 text-neutral-400">{v.plate}{v.km ? ` · ${v.km.toLocaleString()} km` : ""}</span>
              </div>
            </button>

            <button
              onClick={() => setEditingVehicle(v)}
              className="flex-shrink-0 text-h4 font-medium px-2 py-1 rounded-lg hover:bg-indigo-50 transition-colors"
              style={{ color: "#6366f1" }}
            >
              Modifier
            </button>
          </div>
        );
      })}

      {dossierVehicles.length === 0 && (
        <div className="text-h3 text-center text-neutral-400 py-8">Aucun véhicule dans le dossier</div>
      )}

      {editingVehicle && (
        <VehicleEditModal
          vehicle={editingVehicle}
          onSave={(data) => { onSaveVehicle(editingVehicle, data); setEditingVehicle(null); }}
          onClose={() => setEditingVehicle(null)}
          timeline={parentDossier?.timeline || []}
          ordres={parentDossier?.ordres || []}
          documents={parentDossier?.documents}
          dossierId={parentDossier?.id}
        />
      )}
    </div>
  );
}

// ─── Documents tab ──────────────────────────────────────────

const IMAGE_EXTENSIONS = /\.(jpg|jpeg|png|webp|gif|heic|bmp|svg)$/i;

function isImageDoc(d: { type: string; name: string; gcs_path?: string }) {
  return d.type === "image" || IMAGE_EXTENSIONS.test(d.gcs_path || d.name);
}

function PhotoThumbnail({ doc, onClick }: { doc: { id: string; name: string; gcs_path?: string }; onClick: () => void }) {
  const [thumbUrl, setThumbUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!doc.gcs_path) return;
    const isOrdreDoc = doc.gcs_path.startsWith("ordres/");
    const fetchUrl = async () => {
      try {
        // Extract IDs from gcs_path: ordres/{org}/{ordreId}/... or dossiers/{org}/{dossierId}/...
        const parts = doc.gcs_path!.split("/");
        const parentId = parts[2]; // ordreId or dossierId
        const url = isOrdreDoc
          ? await ordresApi.getDocumentUrl(parentId, doc.id)
          : await dossiersApi.getDocumentUrl(parentId, doc.id);
        if (!cancelled) setThumbUrl(url);
      } catch { /* silently fail */ }
    };
    fetchUrl();
    return () => { cancelled = true; };
  }, [doc.gcs_path, doc.id]);

  return (
    <div
      className="w-[120px] h-[120px] rounded-lg overflow-hidden bg-neutral-100 flex items-center justify-center cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
      onClick={onClick}
    >
      {thumbUrl ? (
        <img src={thumbUrl} alt={doc.name} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <Icon1 name="image" size={24} color="#D4D4D4" />
      )}
    </div>
  );
}

function DocumentsTab({
  documents,
  dossierId,
  ordreId,
  onPreview,
  onAddDoc,
  onDeleteDoc,
  devisSignStatus,
  devisDocumentId,
}: {
  documents: Array<{ id: string; name: string; type: string; created_at?: string; gcs_path?: string }>;
  dossierId?: string;
  ordreId?: string;
  onPreview: (doc: any) => void;
  onAddDoc: () => void;
  onDeleteDoc: (doc: { id: string; name: string; gcs_path?: string }) => void;
  devisSignStatus?: string;
  devisDocumentId?: string;
}) {
  const photos = documents.filter(isImageDoc);
  const files = documents.filter((d) => !isImageDoc(d));

  const isPdf = (d: { type: string; name: string; gcs_path?: string }) =>
    d.type === "pdf" || /\.pdf$/i.test(d.gcs_path || d.name);

  return (
    <div className="flex flex-col gap-4">
      {/* Photo gallery */}
      {photos.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-h3 font-medium text-neutral-500">Photos ({photos.length})</span>
          <div className="flex flex-row flex-wrap gap-2">
            {photos.map((p) => (
              <div key={p.id} className="relative group">
                <PhotoThumbnail doc={p} onClick={() => onPreview(p)} />
                <button
                  onClick={() => onDeleteDoc(p)}
                  className="absolute top-1 right-1 p-0.5 rounded-full bg-white/80 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                >
                  <Icon1 name="close" size={12} color="#EF4444" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Files list */}
      {files.length > 0 && (
        <div className="flex flex-col gap-2">
          {photos.length > 0 && <span className="text-h3 font-medium text-neutral-500">Documents ({files.length})</span>}
          {files.map((d) => {
            const isDevisDoc = d.id === devisDocumentId;
            const signedAlready = isDevisDoc && devisSignStatus === "signed";
            return (
              <div key={d.id} className="flex flex-row items-center gap-3 py-2 hover:bg-neutral-50 rounded-lg px-2">
                <div
                  className="flex flex-row items-center gap-3 flex-1 min-w-0 cursor-pointer"
                  onClick={() => onPreview(d)}
                >
                  <div className="w-28 h-28 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0">
                    <Icon1 name={d.type === "pdf" ? "file" : "image"} size={16} color="#A1A1A1" />
                  </div>
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-h3 font-medium truncate">{d.name}</span>
                    {d.created_at && <span className="text-h4 text-neutral-400">{d.created_at}</span>}
                  </div>
                </div>
                <div className="flex flex-row items-center gap-2 flex-shrink-0">
                  {isDevisDoc && devisSignStatus === "pending" && (
                    <span className="text-h4 font-medium px-2 py-0.5 rounded-full" style={{ background: "#DBEAFE", color: "#1E40AF" }}>
                      En attente de signature
                    </span>
                  )}
                  {signedAlready && (
                    <span className="text-h4 font-medium px-2 py-0.5 rounded-full" style={{ background: "#D1FAE5", color: "#065F46" }}>
                      Signé
                    </span>
                  )}
                  <button onClick={() => onPreview(d)} className="p-1 hover:bg-neutral-100 rounded">
                    <Icon1 name="download" size={16} color="#A1A1A1" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); onDeleteDoc(d); }}
                    className="p-1 hover:bg-red-50 rounded"
                  >
                    <Icon1 name="delete" size={16} color="#A1A1A1" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {documents.length === 0 && (
        <div className="text-h3 text-center text-neutral-400 py-8">Aucun document</div>
      )}
      <button
        className="flex flex-row items-center gap-2 text-h3 font-medium text-neutral-600 border border-neutral-200 rounded-lg px-4 py-2 w-fit hover:bg-neutral-50 mt-2"
        onClick={onAddDoc}
      >
        <Icon1 name="plus" size={14} color="#525252" />
        Ajouter un document
      </button>
    </div>
  );
}

// ─── Document preview modal ─────────────────────────────────

// ─── Add document modal (uploads to dossier) ────────────────



// ─── Facture upload modal ────────────────────────────────────


// ─── Status dropdown ────────────────────────────────────────

function OrdreStatusDropdown({
  currentStatus,
  onStatusChange,
}: {
  currentStatus: OrdreStatus;
  onStatusChange: (status: OrdreStatus) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const currentIdx = STATUS_OPTIONS.findIndex((s) => s.value === currentStatus);
  const chip = STATUS_CHIP_STYLES[currentStatus] || { label: currentStatus, bg: "#F4F4F4", color: "#A1A1A1" };

  // Next logical step as shortcut, then full ordered list including current
  const nextOption = currentIdx + 1 < STATUS_OPTIONS.length ? STATUS_OPTIONS[currentIdx + 1] : null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex flex-row items-center gap-1.5 text-h3 font-medium cursor-pointer transition-opacity hover:opacity-80"
        style={{ background: chip.bg, color: chip.color, borderRadius: 5, padding: "4px 14px", lineHeight: "18px" }}
      >
        {chip.label}
        <svg width={8} height={5} viewBox="0 0 8 5" fill="none">
          <path d="M1 1l3 3 3-3" stroke={chip.color} strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute left-0 top-full mt-1 z-50 bg-white rounded-xl shadow-lg border border-neutral-200 overflow-hidden"
          style={{ minWidth: 190, paddingTop: 4, paddingBottom: 4 }}
        >
          {[
            { label: "Preparation", statuses: ["rdv", "a_preparer", "devis_initial", "commande", "pret"] },
            { label: "Travaux", statuses: ["reception", "en_cours", "devis_supp", "accord", "acompte"] },
            { label: "Cloture", statuses: ["fin_de_travaux", "facturation", "solde", "solde_paye", "disponible", "restitue", "arrete"] },
          ].map((group, gi) => (
            <div key={group.label}>
              {gi > 0 && <div style={{ height: 1, background: "#F0F0F0", margin: "4px 12px" }} />}
              <div style={{ padding: "3px 12px 2px", fontFamily: "Poppins", fontSize: 10, fontWeight: 500, color: "#C0C0C0", textTransform: "uppercase", letterSpacing: 0.5 }}>
                {group.label}
              </div>
              {group.statuses.map((sv) => {
                const idx = STATUS_OPTIONS.findIndex((s) => s.value === sv);
                const isCurrent = sv === currentStatus;
                const isPast = idx < currentIdx;
                const isNext = nextOption && sv === nextOption.value;
                const optChip = STATUS_CHIP_STYLES[sv] || { label: sv, bg: "#F4F4F4", color: "#A1A1A1" };
                return (
                  <button
                    key={sv}
                    className={`flex flex-row items-center w-full pr-3 text-left${isCurrent ? " cursor-default" : " hover:bg-neutral-50 transition-colors"}`}
                    style={{ paddingLeft: 20, paddingTop: 2, paddingBottom: 2 }}
                    onClick={isCurrent ? undefined : () => { onStatusChange(sv as OrdreStatus); setOpen(false); }}
                    disabled={isCurrent}
                  >
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: 9999,
                        background: getStatusColor(sv as OrdreStatus),
                        flexShrink: 0,
                        marginRight: 8,
                        opacity: isPast ? 0.45 : 1,
                      }}
                    />
                    <span
                      className={`whitespace-nowrap ${isCurrent ? "font-bold" : "font-medium"}`}
                      style={{
                        fontSize: 12,
                        color: isCurrent ? "#1A1A1A" : isPast ? "#C0C0C0" : "#666666",
                        fontFamily: "Poppins",
                      }}
                    >
                      {optChip.label}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Rental vehicle badge with popover ──────────────────────

function RentalVehicleBadge({ vehicle, reservationId }: { vehicle: Vehicle; reservationId?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const usageLabel = vehicle.usage === "velos" ? "Vélo" : vehicle.usage === "location" ? "Location" : "Prêt";

  return (
    <div className="relative" ref={ref}>
      <button
        className="flex flex-row items-center gap-1.5 text-h3 font-medium rounded-full px-3 py-1 truncate hover:opacity-80 transition-opacity"
        style={{ background: "#DBEAFE", color: "#1E40AF", maxWidth: 220 }}
        onClick={() => setOpen(!open)}
      >
        <Icon1 name={vehicle.usage === "velos" ? "tool" : "car"} size={12} color="#1E40AF" />
        <span className="truncate">{vehicle.plate}</span>
      </button>

      {open && (
        <div
          className="absolute top-full mt-2 left-0 bg-white rounded-xl shadow-lg border border-neutral-200 z-50"
          style={{ width: 260, padding: 16 }}
        >
          <div className="flex flex-col gap-3">
            <div className="flex flex-row items-center gap-3">
              <div className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 8, background: "#DBEAFE" }}>
                <Icon1 name={vehicle.usage === "velos" ? "tool" : "car"} size={18} color="#1E40AF" />
              </div>
              <div className="flex flex-col flex-1 min-w-0">
                <span className="text-h2 font-semibold truncate">{vehicle.name || vehicle.plate}</span>
                <span className="text-h4 text-neutral-400">{vehicle.plate} · {usageLabel}</span>
              </div>
            </div>

            {vehicle.marque && (
              <div className="flex flex-row gap-2 text-h3 text-neutral-500">
                <span>{vehicle.marque} {vehicle.modele}</span>
                {vehicle.coloris && <span>· {vehicle.coloris}</span>}
              </div>
            )}

            {reservationId && (
              <button
                className="flex flex-row items-center justify-center gap-2 w-full py-2 rounded-lg text-h3 font-medium hover:bg-indigo-50 transition-colors"
                style={{ border: "1px solid #C7D2FE", color: "#4F46E5" }}
                onClick={() => {
                  setOpen(false);
                  navigateTo({ route: `/vehicules?reservation=${reservationId}` });
                }}
              >
                <Icon1 name="calendar" size={14} color="#4F46E5" />
                Voir la réservation
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Rental vehicle assignment modal ────────────────────────




// ─── Tracking preview modal (phone frame) ─────────────────


// ─── Sign request modal ────────────────────────────────────

// ─── Booking request card ───────────────────────────────────

const BOOKING_LABELS: Record<string, string> = {
  mecanique: "Mécanique", carrosserie: "Carrosserie", vitrage: "Vitrage", pneumatique: "Pneumatique",
  entretien: "Entretien", reparation: "Réparation", diagnostic: "Diagnostic", controle_technique: "Contrôle technique",
  wait: "Attente sur place", bike: "Vélo", loan: "Véhicule de prêt", rental: "Véhicule de location",
  budget: "Pneus premier prix", mid: "Pneus intermédiaires", premium: "Pneus premium",
  whatsapp: "WhatsApp", email: "Email",
};

function BookingServiceIcon({ type, size = 26, color = "currentColor" }: { type: string; size?: number; color?: string }) {
  const s: React.CSSProperties = { width: size, height: size, flexShrink: 0 };
  const p = { stroke: color, strokeWidth: 1.6, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, fill: "none" };
  if (type === "entretien") return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /></svg>;
  if (type === "reparation") return <svg style={s} viewBox="0 0 24 24" {...p}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" /></svg>;
  if (type === "diagnostic") return <svg style={s} viewBox="0 0 24 24" {...p}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>;
  if (type === "controle_technique") return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></svg>;
  if (type === "pneumatique") return <svg style={s} viewBox="0 0 24 24" {...p}><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="3" /><line x1="12" y1="2" x2="12" y2="9" /><line x1="12" y1="15" x2="12" y2="22" /><line x1="2" y1="12" x2="9" y2="12" /><line x1="15" y1="12" x2="22" y2="12" /></svg>;
  if (type === "carrosserie") return <svg style={s} viewBox="0 0 24 24" {...p}><path d="M6 10l2.5-5h7L18 10" /><rect x="2" y="10" width="20" height="7" rx="2" /><circle cx="7" cy="17" r="2" fill={color} stroke="none" /><circle cx="17" cy="17" r="2" fill={color} stroke="none" /><line x1="12" y1="5" x2="12" y2="10" /></svg>;
  return null;
}

const NEED_CARDS_DISPLAY = [
  { id: "entretien",          iconType: "entretien",         label: "Entretien\nRévision" },
  { id: "reparation",         iconType: "reparation",        label: "Réparation\nPanne" },
  { id: "diagnostic",         iconType: "diagnostic",        label: "Diagnostic" },
  { id: "controle_technique", iconType: "controle_technique", label: "Contrôle\ntechnique" },
  { id: "pneumatique",        iconType: "pneumatique",       label: "Pneus" },
  { id: "carrosserie",        iconType: "carrosserie",       label: "Carrosserie\nVitrage" },
];

const MOBILITY_DISPLAY = [
  { key: "velo", label: "Vélo électrique", sub: "Gratuit" },
  { key: "pret", label: "Véhicule de prêt", sub: "Zone limitée" },
  { key: "location", label: "Véhicule de location", sub: "Liberté / payant" },
  { key: "attente", label: "Attente sur place", sub: "" },
];

function BookingRequestModal({ booking, onClose }: { booking: NonNullable<Ordre["booking_request"]>; onClose: () => void }) {
  const formatDate = (d?: string) => {
    if (!d) return "";
    const [y, m, day] = d.split("-");
    return `${day}/${m}/${y}`;
  };

  // Count completed steps for the progress bar
  const steps = [
    { label: "Véhicule", done: !!(booking.immatriculation) },
    { label: "Besoin", done: !!(booking.intervention_type || booking.intervention) },
    { label: "Créneau", done: !!(booking.deposit_date) },
    { label: "Coordonnées", done: !!(booking.phone || booking.email) },
  ];

  return (
    <BaseModal title="Demande de RDV" startIcon={<Icon1 name="open" size={20} />} onClose={onClose} width="max-w-md">
      <div className="flex flex-col gap-5 p-5" style={{ fontFamily: "Poppins, sans-serif" }}>

        {/* ── Step progress bar ── */}
        <div className="flex flex-row items-center gap-1">
          {steps.map((s, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-1">
              <div className="w-full h-1 rounded-full" style={{ background: s.done ? "#000" : "#E5E5E5" }} />
              <span style={{ fontSize: 9, fontWeight: 500, color: s.done ? "#000" : "#D4D4D4", textTransform: "uppercase" }}>{s.label}</span>
            </div>
          ))}
        </div>

        {/* ── Vehicle card (top) ── */}
        {(booking.immatriculation || !!booking.km) && (
          <div className="flex flex-row items-center gap-3 py-3 px-4 rounded-xl" style={{ background: "#F9FAFB", border: "1.5px solid #E5E5E5" }}>
            <div className="flex items-center justify-center rounded-lg" style={{ width: 40, height: 40, background: "#E5E7EB" }}>
              <Icon1 name="car" size={20} color="#6B7280" />
            </div>
            <div className="flex flex-col">
              {booking.immatriculation && (
                <span style={{ fontSize: 14, fontWeight: 600, color: "#000", letterSpacing: 1 }}>{booking.immatriculation}</span>
              )}
              {!!booking.km && (
                <span style={{ fontSize: 11, color: "#9CA3AF", fontWeight: 500 }}>{Number(booking.km).toLocaleString("fr-FR")} km</span>
              )}
            </div>
          </div>
        )}

        {/* ── Service cards grid ── */}
        <div className="grid grid-cols-3 gap-1.5">
          {NEED_CARDS_DISPLAY.map((card) => {
            const isSelected = card.id === booking.intervention_type;
            return (
              <div
                key={card.id}
                className="flex flex-col items-center justify-center gap-1.5 py-3 px-1 rounded-xl transition-opacity"
                style={{
                  border: isSelected ? "2px solid #000" : "1.5px solid #E5E5E5",
                  background: isSelected ? "#F5F5F5" : "#fff",
                  opacity: isSelected ? 1 : 0.25,
                }}
              >
                <BookingServiceIcon type={card.iconType} size={24} color={isSelected ? "#000" : "#999"} />
                <span style={{ fontWeight: 500, fontSize: 10, color: isSelected ? "#000" : "#666", textAlign: "center", whiteSpace: "pre-line", lineHeight: "13px" }}>{card.label}</span>
              </div>
            );
          })}
        </div>

        {/* ── Intervention pill ── */}
        {booking.intervention && (
          <div className="flex flex-wrap gap-1.5">
            <span className="px-3 py-1.5 rounded-lg" style={{ fontSize: 12, fontWeight: 600, border: "2px solid #000", background: "#F5F5F5", color: "#000" }}>
              {booking.intervention}
            </span>
          </div>
        )}

        {/* ── Dates ── */}
        {(booking.deposit_date || booking.restitution_date) && (
          <div className="flex flex-col gap-1.5">
            <span style={{ fontWeight: 600, fontSize: 11, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5 }}>Dates</span>
            <div className="flex flex-row gap-2">
              {booking.deposit_date && (
                <div className="flex-1 py-2.5 px-3 rounded-xl" style={{ border: "1.5px solid #E5E5E5", background: "#fff" }}>
                  <span style={{ fontSize: 10, color: "#9CA3AF", fontWeight: 500, textTransform: "uppercase" }}>Dépôt</span>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#000" }}>{formatDate(booking.deposit_date)}</div>
                  {booking.deposit_time && <div style={{ fontSize: 12, fontWeight: 500, color: "#6B7280" }}>{booking.deposit_time}</div>}
                </div>
              )}
              {booking.restitution_date && (
                <div className="flex-1 py-2.5 px-3 rounded-xl" style={{ border: "1.5px solid #E5E5E5", background: "#fff" }}>
                  <span style={{ fontSize: 10, color: "#9CA3AF", fontWeight: 500, textTransform: "uppercase" }}>Restitution</span>
                  <div style={{ fontSize: 14, fontWeight: 600, color: "#000" }}>{formatDate(booking.restitution_date)}</div>
                  {booking.restitution_time && <div style={{ fontSize: 12, fontWeight: 500, color: "#6B7280" }}>{booking.restitution_time}</div>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Mobility ── */}
        {!!booking.mobility && (
          <div className="flex flex-col gap-1.5">
            <span style={{ fontWeight: 600, fontSize: 11, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5 }}>Mobilité</span>
            <div className="flex flex-col gap-1.5">
              {MOBILITY_DISPLAY.map((opt) => {
                const isSelected = booking.mobility === opt.key;
                return (
                  <div
                    key={opt.key}
                    className="flex flex-row items-center gap-3 py-2.5 px-3 rounded-xl"
                    style={{
                      border: isSelected ? "2px solid #000" : "1.5px solid #E5E5E5",
                      background: isSelected ? "#F5F5F5" : "#fff",
                      opacity: isSelected ? 1 : 0.25,
                    }}
                  >
                    <span style={{ fontWeight: 500, fontSize: 13, color: isSelected ? "#000" : "#444" }}>{opt.label}</span>
                    {opt.sub && <span style={{ fontSize: 11, color: "#9CA3AF" }}>{opt.sub}</span>}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Tire option ── */}
        {!!booking.tire_option && (
          <div className="flex flex-col gap-1.5">
            <span style={{ fontWeight: 600, fontSize: 11, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5 }}>Type de pneus</span>
            <div className="flex flex-col gap-1.5">
              {[{ value: "budget", label: "Premier prix" }, { value: "mid", label: "Intermédiaires" }, { value: "premium", label: "Premium" }].map((opt) => {
                const isSelected = booking.tire_option === opt.value;
                return (
                  <div
                    key={opt.value}
                    className="flex flex-row items-center gap-3 py-2.5 px-3 rounded-xl"
                    style={{
                      border: isSelected ? "2px solid #000" : "1.5px solid #E5E5E5",
                      background: isSelected ? "#F5F5F5" : "#fff",
                      opacity: isSelected ? 1 : 0.25,
                    }}
                  >
                    <span style={{ fontWeight: 500, fontSize: 13, color: isSelected ? "#000" : "#444" }}>{opt.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Comments ── */}
        {booking.comments && (
          <div className="flex flex-col gap-1.5">
            <span style={{ fontWeight: 600, fontSize: 11, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5 }}>Commentaires</span>
            <div className="py-2.5 px-3 rounded-xl" style={{ border: "1.5px solid #E5E5E5", background: "#fff", fontSize: 13, color: "#000", lineHeight: "20px" }}>{booking.comments}</div>
          </div>
        )}

        {/* ── Contact ── */}
        <div className="flex flex-col gap-2">
          <span style={{ fontWeight: 600, fontSize: 11, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5 }}>Coordonnées</span>
          <div className="flex flex-row gap-2">
            {booking.nom && <div className="flex-1"><TextField label="Nom" value={booking.nom} onChange={() => {}} disabled /></div>}
            {booking.prenom && <div className="flex-1"><TextField label="Prénom" value={booking.prenom} onChange={() => {}} disabled /></div>}
          </div>
          {booking.phone && <TextField label="Téléphone" value={booking.phone} onChange={() => {}} disabled type="phone" />}
          {booking.email && <TextField label="Email" value={booking.email} onChange={() => {}} disabled />}
          {booking.code_postal && <TextField label="Code postal" value={booking.code_postal} onChange={() => {}} disabled />}
        </div>

        {/* ── Channel confirmation ── */}
        {booking.channel && (
          <div className="flex flex-col gap-1.5">
            <span style={{ fontWeight: 600, fontSize: 11, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 0.5 }}>Confirmation via</span>
            <div className="flex flex-row gap-1.5">
              {[
                { value: "whatsapp", label: "WhatsApp" },
                { value: "email", label: "Email" },
              ].map((ch) => {
                const isActive = booking.channel === ch.value;
                return (
                  <div
                    key={ch.value}
                    className="flex items-center gap-2 rounded-xl px-3 py-2"
                    style={{
                      border: isActive ? "2px solid #000" : "1.5px solid #E5E5E5",
                      background: isActive ? "#F5F5F5" : "#fff",
                      opacity: isActive ? 1 : 0.25,
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 500, color: isActive ? "#000" : "#444" }}>{ch.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </BaseModal>
  );
}

// ─── Main screen ───────────────────────────────────────────

export default function OrdreDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState("fil");
  // Pastille on the Documents tab when a client sent a file (WhatsApp).
  const [docsSeen, setDocsSeen] = useState(false);
  const [search, setSearch] = useState("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteInServiceBox, setDeleteInServiceBox] = useState(true);
  const [deleteDocConfirm, setDeleteDocConfirm] = useState<{ id: string; name: string; gcs_path?: string } | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showAddDoc, setShowAddDoc] = useState(false);
  const [showTrackingPreview, setShowTrackingPreview] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<{ doc: any; url: string } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showRentalModal, setShowRentalModal] = useState(false);
  const [rentalVehicle, setRentalVehicle] = useState<Vehicle | null>(null);
  const [showEditRentalRes, setShowEditRentalRes] = useState(false);
  const [showContratModal, setShowContratModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState<"acompte" | "solde" | null>(null);
  const [manualPaymentModal, setManualPaymentModal] = useState<
    { mode: "create" | "mark"; payment?: Payment; defaultType?: "acompte" | "solde" } | null
  >(null);
  const [showFactureUpload, setShowFactureUpload] = useState(false);
  const [sbEstimationLoading, setSbEstimationLoading] = useState(false);
  const [sbExtraction, setSbExtraction] = useState<EstimateExtraction | null>(null);
  const [showServiceBoxEstimation, setShowServiceBoxEstimation] = useState(false);
  const [sbForfaitChoices, setSbForfaitChoices] = useState<ForfaitChoice[] | null>(null);
  const [sbForfaitSel, setSbForfaitSel] = useState<Record<string, string>>({});
  const [sbForfaitSubmitting, setSbForfaitSubmitting] = useState(false);
  const actionsMenuCloseRef = useRef<(() => void) | null>(null);
  const [showSecondaryModal, setShowSecondaryModal] = useState(false);
  const [flagReasonModal, setFlagReasonModal] = useState<{ flag: OrdreFlag; newFlags: OrdreFlag[] } | null>(null);
  const [flagReasonText, setFlagReasonText] = useState("");
  const [showServiceBoxModal, setShowServiceBoxModal] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [serviceboxProxyAvailable, setServiceboxProxyAvailable] = useState(false);
  const [showProxyToast, setShowProxyToast] = useState(false);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [recalls, setRecalls] = useState<{ code: string; libelle: string; status: string }[]>([]);
  const [recheckingRecalls, setRecheckingRecalls] = useState(false);
  const removeOrdres = useOrdreStore((s) => s.removeOrdres);
  const userData = useDataStore((s) => s.userData);
  const orgData = useDataStore((s) => s.orgData);
  const orgMembers = useDataStore((s) => s.orgMembers);
  const ordres = useOrdreStore((s) => s.ordres);
  const fetchAtelierData = useAtelierStore((s) => s.fetchData);
  const atelierJobs = useAtelierStore((s) => s.jobs);
  const atelierWorkers = useAtelierStore((s) => s.workers);
  const atelierCharges = useAtelierStore((s) => s.charges);

  const [parentDossier, setParentDossier] = useState<Dossier | null>(null);
  const [configuredInterventions, setConfiguredInterventions] = useState<Intervention[]>([]);

  useEffect(() => {
    if (orgData?.id) {
      useTagStore.getState().fetchTags(orgData.id);
      fetchAtelierData(orgData.id);
      interventionsApi.list(orgData.id).then(setConfiguredInterventions).catch(() => {});
    }
  }, [orgData?.id]);

  // Check if ServiceBox proxy is running locally (poll until found)
  useEffect(() => {
    if (!orgData?.servicebox?.enabled) return;
    let cancelled = false;
    const check = () => {
      serviceboxProxy.checkHealth().then((r) => {
        if (cancelled) return;
        setServiceboxProxyAvailable(!!r);
        if (r && !_proxyToastShownThisSession) {
          _proxyToastShownThisSession = true;
          setShowProxyToast(true);
          setTimeout(() => { if (!cancelled) setShowProxyToast(false); }, 3500);
        }
        if (!r) setTimeout(() => { if (!cancelled) check(); }, 60000);
      });
    };
    check();
    return () => { cancelled = true; };
  }, [orgData?.servicebox?.enabled]);

  const ordre = useMemo(() => ordres.find((o) => o.id === id), [id, ordres]);
  const [loadingOrdre, setLoadingOrdre] = useState(!ordre);

  // Fetch enriched ordre (with contacts/vehicle from dossier) on mount
  useEffect(() => {
    if (id) {
      setLoadingOrdre(true);
      ordresApi.get(id).then((enriched) => {
        useOrdreStore.getState().updateOrdre(enriched);
      }).catch(() => {}).finally(() => setLoadingOrdre(false));
    }
  }, [id]);

  const refreshOrdre = () => {
    if (id) {
      ordresApi.get(id).then((enriched) => {
        useOrdreStore.getState().updateOrdre(enriched);
      }).catch(() => {});
    }
  };

  // Live-listen to this ordre's Firestore doc so status/devis changes made
  // elsewhere (devis sent → devis_initial, client signs → commande, backend
  // status automation) reflect in the open page without a manual refresh.
  useEffect(() => {
    if (!id) return;
    let first = true;
    const unsub = onSnapshot(
      doc(firebaseService.db, "ordres", id),
      (snap) => {
        // Skip the initial snapshot — the mount fetch already loaded this data.
        if (first) {
          first = false;
          return;
        }
        if (!snap.exists()) return;
        const data = snap.data() as Partial<Ordre>;
        const current = useOrdreStore.getState().ordres.find((o) => o.id === id);
        if (!current) return;
        // Merge only the live-relevant fields; keep API-enriched fields
        // (contacts, vehicle from dossier) intact.
        useOrdreStore.getState().updateOrdre({
          ...current,
          status: (data.status as OrdreStatus) ?? current.status,
          devis_sign_status: data.devis_sign_status ?? current.devis_sign_status,
          devis_sign_token: data.devis_sign_token ?? current.devis_sign_token,
          devis_signed: data.devis_signed ?? current.devis_signed,
          intervention_groups: data.intervention_groups ?? current.intervention_groups,
        });
      },
      (err) => {
        console.error("Ordre realtime listener error:", err);
      }
    );
    return () => unsub();
  }, [id]);

  const sbCreds = () => ({
    username: orgData?.servicebox?.username || "",
    password: orgData?.servicebox?.password || "",
    site_code: orgData?.servicebox?.site_code,
  });

  // When the DMS asks to choose a forfait variant, open the picker. Returns true if handled.
  const handleForfaitChoiceResult = (result: { needs_forfait_choice?: boolean; forfait_choices?: ForfaitChoice[] }) => {
    if (!result.needs_forfait_choice) return false;
    const choices = result.forfait_choices || [];
    const sel: Record<string, string> = {};
    for (const c of choices) {
      if (!(c.field in sel) || c.selected) sel[c.field] = c.code;
    }
    setSbForfaitChoices(choices);
    setSbForfaitSel(sel);
    setShowServiceBoxEstimation(false);
    return true;
  };

  const importEstimationHtml = async (html: string) => {
    if (!ordre) return;
    const extraction = await estimateExtractionApi.importServicebox(ordre.id, html);
    setSbExtraction(extraction);
    const refreshed = await ordresApi.get(ordre.id);
    useOrdreStore.getState().updateOrdre(refreshed);
  };

  const submitForfaitChoice = async () => {
    if (!ordre || !orgData?.servicebox || !ordre.servicebox_dossier_id) return;
    setSbForfaitSubmitting(true);
    try {
      const selections = Object.entries(sbForfaitSel).map(([field, value]) => ({ field, value }));
      const result = await serviceboxProxy.chooseForfait(sbCreds(), ordre.servicebox_dossier_id, selections, () => showNotification({ type: "default", message: PROXY_BUSY_MESSAGE }));
      if (handleForfaitChoiceResult(result)) return; // still ambiguous (another group)
      if (!result.success || !result.html) {
        showNotification({ type: "error", message: result.error || "Impossible de récupérer le devis" });
        return;
      }
      setSbForfaitChoices(null);
      setShowServiceBoxEstimation(true);
      setSbEstimationLoading(true);
      await importEstimationHtml(result.html);
    } catch (e: any) {
      showNotification({ type: "error", message: e?.response?.data?.detail || e?.message || "Erreur" });
    } finally {
      setSbForfaitSubmitting(false);
      setSbEstimationLoading(false);
    }
  };

  const handleImportServiceBoxEstimation = async () => {
    if (!ordre || !orgData?.servicebox) return;
    if (!ordre.servicebox_dossier_id) {
      showNotification({ type: "error", message: "Aucun dossier ServiceBox lié à cet OR. Transférez d'abord vers ServiceBox." });
      return;
    }
    setSbExtraction(null);
    setShowServiceBoxEstimation(true);
    setSbEstimationLoading(true);
    actionsMenuCloseRef.current?.();
    try {
      const result = await serviceboxProxy.fetchEstimation(sbCreds(), ordre.servicebox_dossier_id, () => showNotification({ type: "default", message: PROXY_BUSY_MESSAGE }));
      if (handleForfaitChoiceResult(result)) return;
      if (!result.success || !result.html) {
        showNotification({ type: "error", message: result.error || "Impossible de récupérer l'estimation" });
        setShowServiceBoxEstimation(false);
        return;
      }
      await importEstimationHtml(result.html);
    } catch (e: any) {
      showNotification({ type: "error", message: e?.response?.data?.detail || e?.message || "Erreur" });
      setShowServiceBoxEstimation(false);
    } finally {
      setSbEstimationLoading(false);
    }
  };

  const fetchPayments = () => {
    if (id) paymentsApi.listForOrdre(id).then(setPayments).catch(() => {});
  };

  useEffect(() => { fetchPayments(); }, [id]);

  // Poll payment status every 30s while there are pending/initiated payments
  useEffect(() => {
    const hasPending = payments.some((p) => ["initiated", "pending"].includes(p.state));
    if (!hasPending) return;
    const interval = setInterval(fetchPayments, 30_000);
    return () => clearInterval(interval);
  }, [payments]);

  // Fetch parent dossier
  useEffect(() => {
    if (ordre?.dossier_id) {
      dossiersApi.get(ordre.dossier_id).then(setParentDossier).catch(() => {});
    }
  }, [ordre?.dossier_id]);

  useEffect(() => {
    if (ordre?.rental_vehicle_id) {
      vehiclesApi.get(ordre.rental_vehicle_id).then(setRentalVehicle).catch(() => {});
    } else {
      setRentalVehicle(null);
    }
  }, [ordre?.rental_vehicle_id]);

  // Recall campaigns: seed from the ordre, fetch stored entries, and if none are
  // known yet check the vehicle's VIN live via ServiceBox. Robust across ordres
  // that were never created through the online-booking flow.
  useEffect(() => {
    if (!id || !ordre) return;
    let cancelled = false;

    const mergeByCode = (
      lists: { code: string; libelle: string; status: string }[][],
    ) => {
      const byCode = new Map<string, { code: string; libelle: string; status: string }>();
      for (const list of lists) {
        for (const c of list) {
          const key = c.code || c.libelle;
          if (key && !byCode.has(key)) byCode.set(key, c);
        }
      }
      return Array.from(byCode.values());
    };

    const embedded = (ordre.recall_campaigns || []).map((c) => ({
      code: c.code,
      libelle: c.libelle,
      status: c.status || "detected",
    }));
    setRecalls(embedded);

    (async () => {
      let result = embedded;

      try {
        const stored = await recallCampaignsApi.listForOrdre(id);
        result = mergeByCode([
          result,
          stored.map((s) => ({ code: s.campaign_code, libelle: s.campaign_title, status: s.status })),
        ]);
      } catch { /* best-effort */ }

      // Nothing known yet → check the VIN live (ServiceBox), if available.
      // Throttled: skip if we already checked this ordre within the last 7 days.
      const vin =
        ordre.vehicle?.vin ||
        parentDossier?.vehicles?.find((v) => v.id === ordre.vehicle_id)?.vin ||
        "";
      const checkedAt = ordre.recall_checked_at ? Date.parse(ordre.recall_checked_at) : 0;
      const stale = !checkedAt || Date.now() - checkedAt > 7 * 24 * 3600 * 1000;
      if (result.length === 0 && stale && vin && orgData?.id && orgData?.servicebox?.enabled) {
        try {
          const found = await recallCampaignsApi.checkVin(
            orgData.id,
            vin,
            ordre.immatriculation || "",
            id,
          );
          result = mergeByCode([
            result,
            found.map((f) => ({ code: f.code, libelle: f.libelle, status: "detected" })),
          ]);
        } catch { /* best-effort */ }
      }

      if (!cancelled) setRecalls(result);
    })();

    return () => { cancelled = true; };
  }, [
    id,
    ordre?.recall_campaigns,
    ordre?.recall_checked_at,
    ordre?.vehicle?.vin,
    ordre?.vehicle_id,
    ordre?.immatriculation,
    parentDossier,
    orgData?.id,
    orgData?.servicebox?.enabled,
  ]);

  // Force a live recall-campaign check (ignores the 7-day throttle). The backend
  // resolves the VIN from the plate when the vehicle has none stored.
  const handleRecheckRecalls = async () => {
    if (!ordre || !orgData?.id) return;
    setRecheckingRecalls(true);
    try {
      const vin =
        ordre.vehicle?.vin ||
        parentDossier?.vehicles?.find((v) => v.id === ordre.vehicle_id)?.vin ||
        "";
      const found = await recallCampaignsApi.checkVin(
        orgData.id,
        vin,
        ordre.immatriculation || "",
        ordre.id,
      );
      setRecalls((prev) => {
        const byCode = new Map(prev.map((c) => [c.code || c.libelle, c]));
        found.forEach((f) => {
          const key = f.code || f.libelle;
          if (!byCode.has(key)) byCode.set(key, { code: f.code, libelle: f.libelle, status: "detected" });
        });
        return Array.from(byCode.values());
      });
      showNotification(
        found.length > 0
          ? { type: "success", message: `${found.length} campagne(s) de rappel détectée(s)` }
          : { type: "default", message: "Aucune campagne de rappel détectée pour ce véhicule" },
      );
    } catch {
      showNotification({ type: "error", message: "Échec de la vérification des campagnes" });
    } finally {
      setRecheckingRecalls(false);
    }
  };

  const openDocPreview = async (doc: { id: string; gcs_path?: string }) => {
    if (!doc.gcs_path || !ordre) return;
    setLoadingPreview(true);
    try {
      const isOrdreDoc = doc.gcs_path.startsWith("ordres/") || doc.gcs_path.startsWith("estimate_extractions/");
      const url = isOrdreDoc
        ? await ordresApi.getDocumentUrl(ordre.id, doc.id)
        : parentDossier
          ? await dossiersApi.getDocumentUrl(parentDossier.id, doc.id)
          : null;
      if (url) setPreviewDoc({ doc, url });
    } catch {
      showNotification({ type: "error", message: "Impossible de charger l'aperçu" });
    } finally {
      setLoadingPreview(false);
    }
  };

  const hasFacture = useMemo(
    () => (ordre?.documents || []).some((d) => (d.type || "").toLowerCase().replace(" ", "_") === "facture"),
    [ordre?.documents],
  );

  const ordreAtelierCharges = useMemo(() => {
    if (!id) return [];
    const workerMap = new Map(atelierWorkers.map((w) => [w.id, w.name]));
    const jobRows = atelierJobs
      .filter((j) => j.ordre_id === id)
      .map((j) => ({
        id: j.id,
        worker_name: workerMap.get(j.workerId) || "—",
        specialty: j.specialty,
        date: j.date,
        start_hour: j.startHour,
        duration_hours: j.durationHours,
        is_placed: true,
      }));
    const chargeRows = atelierCharges
      .filter((c) => c.ordre_id === id)
      .map((c) => ({
        id: c.id,
        worker_name: "Non assigné",
        specialty: c.specialty,
        date: "—",
        start_hour: 0,
        duration_hours: c.hours,
        is_placed: false,
      }));
    return [...jobRows, ...chargeRows];
  }, [atelierJobs, atelierWorkers, atelierCharges, id]);

  // Display-only merge of everyone "on" this ordre: people assigned via the
  // dropdown (assigned_to_ids) unioned with mechanics scheduled in the atelier
  // planning. Deduped by member id (atelier worker.id === org member id).
  // `assigned` = picked in the dropdown; `scheduled` = has an atelier job.
  const peopleOnOrdre = useMemo(() => {
    if (!id) return [];
    const assignedIds = getAssignedIds(ordre);
    const scheduledIds = new Set<string>();
    const scheduledOrder: string[] = [];
    for (const j of atelierJobs) {
      if (j.ordre_id === id && j.workerId && !scheduledIds.has(j.workerId)) {
        scheduledIds.add(j.workerId);
        scheduledOrder.push(j.workerId);
      }
    }
    const order: string[] = [];
    const seen = new Set<string>();
    for (const pid of assignedIds.concat(scheduledOrder)) {
      if (pid && !seen.has(pid)) { seen.add(pid); order.push(pid); }
    }
    return order.map((pid) => {
      const w = atelierWorkers.find((ww) => ww.id === pid);
      const m = orgMembers.find((mm) => mm.id === pid);
      const name = w?.name || (m ? (m.infos.display_name || m.infos.full_name) : pid) || "?";
      const specialty = w?.specialty || (m?.config as any)?.team || "autre";
      return {
        id: pid,
        name,
        specialty,
        assigned: assignedIds.includes(pid),
        scheduled: scheduledIds.has(pid),
      };
    });
  }, [ordre, atelierJobs, atelierWorkers, orgMembers, id]);

  if (!ordre) {
    if (loadingOrdre) {
      return (
        <div className="flex flex-col h-screen" style={{ background: "#FAFAFA" }}>
          <div className="w-full h-1 overflow-hidden" style={{ background: "#E0E7FF" }}>
            <div className="h-full rounded-full animate-loading-bar" style={{ background: "#3B82F6", width: "40%" }} />
          </div>
          <style>{`
            @keyframes loading-bar { 0% { transform: translateX(-100%); } 100% { transform: translateX(350%); } }
            .animate-loading-bar { animation: loading-bar 1.2s ease-in-out infinite; }
          `}</style>
          <div className="flex-1 p-6 flex flex-col gap-4">
            <div className="h-8 w-64 rounded-lg animate-pulse" style={{ background: "#E5E7EB" }} />
            <div className="h-4 w-48 rounded-lg animate-pulse" style={{ background: "#E5E7EB" }} />
            <div className="flex gap-4 mt-4">
              <div className="h-32 flex-1 rounded-xl animate-pulse" style={{ background: "#E5E7EB" }} />
              <div className="h-32 flex-1 rounded-xl animate-pulse" style={{ background: "#E5E7EB" }} />
            </div>
            <div className="h-64 w-full rounded-xl animate-pulse mt-2" style={{ background: "#E5E7EB" }} />
          </div>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: "#FAFAFA" }}>
        <span className="text-h2 text-neutral-400">Ordre introuvable</span>
      </div>
    );
  }

  const primaryVehicle = ordre.vehicle
    || (ordre.vehicle_id && parentDossier?.vehicles?.find((v) => v.id === ordre.vehicle_id))
    || null;

  return (
    <div className="flex flex-row h-screen overflow-hidden" style={{ background: "#FAFAFA" }}>
      {flagReasonModal && ordre && (
        <BaseModal
          title={`Drapeau « ${ORDRE_FLAG_CONFIG[flagReasonModal.flag].label} »`}
          startIcon={<Icon1 name={ORDRE_FLAG_CONFIG[flagReasonModal.flag].icon} size={20} color={ORDRE_FLAG_CONFIG[flagReasonModal.flag].color} />}
          onClose={() => setFlagReasonModal(null)}
        >
          <div className="flex flex-col gap-3 p-4">
            <div className="text-h3 text-neutral-500">Raison (optionnel)</div>
            <input
              type="text"
              value={flagReasonText}
              onChange={(e) => setFlagReasonText(e.target.value)}
              placeholder="Ex : en attente de pièces..."
              className="w-full px-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  (async () => {
                    try {
                      const updated = await ordresApi.update(ordre.id, {
                        flags: flagReasonModal.newFlags,
                        ...(flagReasonText ? { flag_reason: flagReasonText } : {}),
                      } as any);
                      useOrdreStore.getState().updateOrdre(updated);
                      showNotification({ type: "success", message: `Drapeau « ${ORDRE_FLAG_CONFIG[flagReasonModal.flag].label} » ajouté` });
                    } catch {
                      showNotification({ type: "error", message: "Erreur" });
                    }
                    setFlagReasonModal(null);
                  })();
                }
              }}
            />
          </div>
          <div className="border-t-1 p-4 flex flex-row justify-end gap-2">
            <SecondaryButton variant="small" onClick={() => setFlagReasonModal(null)}>
              Annuler
            </SecondaryButton>
            <Button
              variant="small"
              label="Confirmer"
              onClick={async () => {
                try {
                  const updated = await ordresApi.update(ordre.id, {
                    flags: flagReasonModal.newFlags,
                    ...(flagReasonText ? { flag_reason: flagReasonText } : {}),
                  } as any);
                  useOrdreStore.getState().updateOrdre(updated);
                  showNotification({ type: "success", message: `Drapeau « ${ORDRE_FLAG_CONFIG[flagReasonModal.flag].label} » ajouté` });
                } catch {
                  showNotification({ type: "error", message: "Erreur" });
                }
                setFlagReasonModal(null);
              }}
            />
          </div>
        </BaseModal>
      )}
      {showDeleteConfirm && (
        <ConfirmationModal
          title="Supprimer cet ordre ?"
          message="L'ordre ainsi que ses rendez-vous (réception et restitution) et ses charges atelier seront définitivement supprimés."
          onClose={() => setShowDeleteConfirm(false)}
          extraContent={ordre?.servicebox_rdv_id ? (
            <div className="flex flex-row items-center gap-3 p-2.5 rounded-lg bg-neutral-50 border border-neutral-200">
              <div className="flex flex-col flex-1">
                <span className="text-h4 font-medium text-neutral-800">Supprimer aussi dans ServiceBox</span>
                <span className="text-h5 text-neutral-400">Annule également le RDV dans le planning ServiceBox</span>
              </div>
              <Switch checked={deleteInServiceBox} onChange={setDeleteInServiceBox} />
            </div>
          ) : undefined}
          onConfirm={async () => {
            // Optionally delete the linked RDV in ServiceBox first (best-effort).
            if (deleteInServiceBox && ordre?.servicebox_rdv_id && orgData?.servicebox) {
              const sb = orgData.servicebox;
              try {
                await serviceboxProxy.deleteRdv({ username: sb.username || "", password: sb.password || "", site_code: sb.site_code }, ordre.servicebox_rdv_id, () => showNotification({ type: "default", message: PROXY_BUSY_MESSAGE }));
              } catch (e: any) {
                showNotification({ type: "error", message: `Suppression ServiceBox échouée : ${e?.message || "erreur"}` });
              }
            }
            if (id) {
              removeOrdres([id]);
              showNotification({ type: "success", message: "Ordre supprimé avec succès" });
              navigateTo({ route: "/ordres" });
            }
            setShowDeleteConfirm(false);
          }}
          confirmText="CONFIRMER"
        />
      )}
      {deleteDocConfirm && ordre && (
        <ConfirmationModal
          title={`Supprimer « ${deleteDocConfirm.name} » ?`}
          message="Ce document sera définitivement supprimé."
          onClose={() => setDeleteDocConfirm(null)}
          onConfirm={async () => {
            try {
              const isOrdreDoc = (ordre.documents || []).some((d) => d.id === deleteDocConfirm.id);
              if (isOrdreDoc) {
                const updated = await ordresApi.deleteDocument(ordre.id, deleteDocConfirm.id);
                useOrdreStore.getState().updateOrdre(updated);
              } else if (ordre.dossier_id) {
                const updated = await dossiersApi.deleteDocument(ordre.dossier_id, deleteDocConfirm.id);
                setParentDossier(updated);
              }
              showNotification({ type: "success", message: "Document supprimé" });
            } catch {
              showNotification({ type: "error", message: "Erreur lors de la suppression" });
            }
            setDeleteDocConfirm(null);
          }}
          confirmText="CONFIRMER"
        />
      )}
      {showEditModal && ordre && (
        <OrdreModal
          ordre={ordre}
          onClose={() => setShowEditModal(false)}
          onSave={() => setShowEditModal(false)}
          onDelete={() => { setShowEditModal(false); navigateTo({ route: "/ordres" }); }}
        />
      )}
      {showBookingModal && ordre?.booking_request && (
        <BookingRequestModal booking={ordre.booking_request} onClose={() => setShowBookingModal(false)} />
      )}
      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-y-auto overflow-x-hidden min-w-0">
        {/* Header */}
        <div className="flex flex-row items-start justify-between px-6 pt-8 pb-4">
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            <Text1>
              {ordre.dossier_id ? (
                <span
                  className="cursor-pointer hover:text-[#5C6BFF] transition-colors"
                  onClick={() => navigateTo({ route: `/dossier/${ordre.dossier_id}` })}
                >
                  {ordre.name}
                </span>
              ) : ordre.name}
            </Text1>
            <div className="flex flex-row items-center gap-2">
              {ordre.or_number ? (
                <span className="text-h3 font-semibold px-2 py-0.5 rounded" style={{ background: "#D1FAE5", color: "#065F46" }}>
                  {ordre.num}
                </span>
              ) : (
                <span className="text-h3 text-neutral-300 font-mono">{ordre.num}</span>
              )}
              {orgData?.servicebox?.enabled && !ordre.or_number && (
                <button
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-h4 font-medium transition-colors hover:bg-neutral-100 border border-neutral-200"
                  disabled={!serviceboxProxyAvailable}
                  onClick={() => setShowServiceBoxModal(true)}
                  style={{ opacity: serviceboxProxyAvailable ? 1 : 0.4 }}
                >
                  <Icon1 name="sett-servicebox" size={14} />
                  <span>Transférer vers ServiceBox</span>
                </button>
              )}
            </div>

            {/* Dates row + rating */}
            <div className="flex flex-row items-center gap-4 mt-1">
              {ordre.reception_date && (
                <div className="flex flex-row items-center gap-1.5">
                  <Icon1 name="horloge" size={14} color="#A1A1A1" />
                  <span className="text-h4 text-neutral-500">{formatTimestamp(ordre.reception_date)}</span>
                </div>
              )}
              {ordre.restitution && (
                <div className="flex flex-row items-center gap-1.5">
                  <Icon1 name="horloge" size={14} color="#A1A1A1" />
                  <span className="text-h4 text-neutral-500">{formatTimestamp(ordre.restitution)}</span>
                </div>
              )}
              {ordre.client_rating != null && ordre.client_rating > 0 && (
                <div className="flex flex-row items-center gap-1 px-2 py-0.5 rounded-full" style={{ background: ordre.client_rating >= 4 ? "#F0FDF4" : ordre.client_rating >= 3 ? "#FFFBEB" : "#FEF2F2" }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill={ordre.client_rating >= 4 ? "#22C55E" : ordre.client_rating >= 3 ? "#F59E0B" : "#EF4444"}>
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  <span className="text-h4 font-medium" style={{ color: ordre.client_rating >= 4 ? "#22C55E" : ordre.client_rating >= 3 ? "#F59E0B" : "#EF4444" }}>{ordre.client_rating}/5</span>
                </div>
              )}
            </div>

            {/* Status + assigned badges */}
            <div className="flex flex-row items-center gap-2 mt-1">
              <OrdreStatusDropdown
                currentStatus={ordre.status}
                onStatusChange={async (status) => {
                  try {
                    const updates: Partial<Ordre> = { status };
                    const autoTracking = STATUS_TO_TRACKING[status];
                    if (autoTracking) updates.tracking_step = autoTracking;
                    const updated = await ordresApi.update(ordre.id, updates);
                    useOrdreStore.getState().updateOrdre(updated);
                    showNotification({ type: "success", message: "Statut mis à jour" });
                    // Prompt to upload facture when moving to "facturation" without one
                    if (status === "facturation" && !hasFacture) {
                      setShowFactureUpload(true);
                    }
                  } catch {
                    showNotification({ type: "error", message: "Erreur lors de la mise à jour" });
                  }
                }}
              />
              <AssignDropdownShared
                assignedIds={getAssignedIds(ordre)}
                onChange={async (userIds) => {
                  try {
                    const updated = await ordresApi.update(ordre.id, { assigned_to_ids: userIds });
                    useOrdreStore.getState().updateOrdre(updated);
                    showNotification({ type: "success", message: userIds.length ? "Assignation mise à jour" : "Assignation retirée" });
                  } catch {
                    showNotification({ type: "error", message: "Erreur lors de l'assignation" });
                  }
                }}
              />
              {peopleOnOrdre.map((p) => {
                const color = p.specialty === "carrosserie" ? "#92400E" : p.specialty === "mecanique" ? "#1E40AF" : "#374151";
                const bg = p.specialty === "carrosserie" ? "#FEF3C7" : p.specialty === "mecanique" ? "#DBEAFE" : "#F3F4F6";
                // Scheduled-only people (planning, not picked in the dropdown) get the
                // atelier icon; assigned people get the user icon.
                const icon = p.assigned ? "user" : "side-ordres";
                const title = p.assigned && p.scheduled
                  ? "Assigné · planifié à l'atelier"
                  : p.assigned
                    ? "Assigné"
                    : "Planifié à l'atelier";
                return (
                  <span
                    key={p.id}
                    title={title}
                    className="flex flex-row items-center gap-1.5 text-h3 font-medium rounded-full px-3 py-1"
                    style={{ background: bg, color }}
                  >
                    <Icon1 name={icon} size={12} color={color} />
                    {p.name}
                  </span>
                );
              })}
              {ordre.devis_sign_status === "pending" && (
                <span className="text-h3 font-medium rounded-full px-3 py-1" style={{ background: "#DBEAFE", color: "#1E40AF" }}>
                  Devis en attente de signature
                </span>
              )}
              {ordre.devis_sign_status === "signed" && (
                <span className="text-h3 font-medium rounded-full px-3 py-1" style={{ background: "#D1FAE5", color: "#065F46" }}>
                  Devis signé
                </span>
              )}
              {ordre.amount != null && (
                <span className="text-h3 font-medium rounded-full px-3 py-1" style={{ background: "#EDE9FE", color: "#5B21B6" }}>
                  {ordre.amount.toFixed(2)} € TTC
                </span>
              )}
              {rentalVehicle && (
                <RentalVehicleBadge vehicle={rentalVehicle} reservationId={ordre.rental_reservation_id} />
              )}
              {ordre.booking_request && (
                <button
                  onClick={() => setShowBookingModal(true)}
                  className="text-h4 font-medium rounded-full px-3 py-1 hover:opacity-80 transition-opacity"
                  style={{ background: "#EEF2FF", color: "#6366F1", border: "1px solid #E0E7FF" }}
                >
                  Demande RDV
                </button>
              )}
              <InlineTagEditor
                scope="ordre"
                tagIds={ordre.tag_ids || []}
                onSave={async (ids) => {
                  try {
                    const updated = await ordresApi.update(ordre.id, { tag_ids: ids });
                    useOrdreStore.getState().updateOrdre(updated);
                  } catch {}
                }}
              />
            </div>

            {/* Flags banner */}
            {ordre.flags && ordre.flags.length > 0 && (
              <div className="flex flex-col gap-1 mt-2">
                {ordre.flags.map((flag) => {
                  const cfg = ORDRE_FLAG_CONFIG[flag];
                  if (!cfg) return null;
                  return (
                    <div
                      key={flag}
                      className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg"
                      style={{ background: cfg.bg, border: `1px solid ${cfg.color}30` }}
                    >
                      <Icon1 name={cfg.icon} size={16} color={cfg.color} />
                      <span className="text-h3 font-semibold" style={{ color: cfg.color }}>
                        {cfg.label}
                      </span>
                      {ordre.flag_reason && (
                        <span className="text-h4 font-normal" style={{ color: cfg.color }}>
                          — {ordre.flag_reason}
                        </span>
                      )}
                      <button
                        className="ml-auto text-h4 font-medium hover:underline"
                        style={{ color: cfg.color }}
                        onClick={async () => {
                          try {
                            const newFlags = (ordre.flags || []).filter((f) => f !== flag);
                            const updated = await ordresApi.update(ordre.id, {
                              flags: newFlags,
                              ...(newFlags.length === 0 ? { flag_reason: "" } : {}),
                            } as any);
                            useOrdreStore.getState().updateOrdre(updated);
                            showNotification({ type: "success", message: "Drapeau retiré" });
                          } catch {
                            showNotification({ type: "error", message: "Erreur" });
                          }
                        }}
                      >
                        Retirer
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Recall campaigns alert */}
            {recalls.length > 0 && (
              <div className="flex flex-col gap-1 px-3 py-2 rounded-lg mt-2" style={{ background: "#FEF2F2", border: "1px solid #FECACA" }}>
                <div className="flex flex-row items-center gap-2">
                  <Icon1 name="warning" size={14} color="#DC2626" />
                  <span className="text-h3 font-semibold" style={{ color: "#DC2626" }}>
                    Campagne de rappel ({recalls.length})
                  </span>
                  {orgData?.servicebox?.enabled && (
                    <button
                      onClick={handleRecheckRecalls}
                      disabled={recheckingRecalls}
                      className="ml-auto text-h5 font-medium px-2 py-0.5 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                      style={{ color: "#991B1B" }}
                    >
                      {recheckingRecalls ? "Vérification..." : "Re-vérifier"}
                    </button>
                  )}
                </div>
                {recalls.map((c) => (
                  <div key={c.code || c.libelle} className="flex flex-row items-center gap-2">
                    <span className="text-h4" style={{ color: "#991B1B" }}>
                      {c.code} — {c.libelle}
                    </span>
                    <span
                      className="text-h5 font-medium rounded-full px-2 py-0.5"
                      style={{
                        background: c.status === "treated" ? "#D1FAE5" : c.status === "acknowledged" ? "#FEF3C7" : "#FEE2E2",
                        color: c.status === "treated" ? "#065F46" : c.status === "acknowledged" ? "#92400E" : "#991B1B",
                      }}
                    >
                      {c.status === "treated" ? "Traité" : c.status === "acknowledged" ? "Pris en compte" : "Détecté"}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* No recall detected — allow a manual (forced) re-check */}
            {recalls.length === 0 && orgData?.servicebox?.enabled && (
              <div className="flex flex-row items-center gap-2 px-3 py-2 rounded-lg mt-2" style={{ background: "#F9FAFB", border: "1px solid #E5E7EB" }}>
                <span className="text-h4" style={{ color: "#6B7280" }}>Aucune campagne de rappel détectée</span>
                <button
                  onClick={handleRecheckRecalls}
                  disabled={recheckingRecalls}
                  className="ml-auto text-h5 font-medium px-2 py-0.5 rounded-lg hover:bg-neutral-100 transition-colors disabled:opacity-50"
                  style={{ color: "#5C6BFF" }}
                >
                  {recheckingRecalls ? "Vérification..." : "Vérifier les campagnes"}
                </button>
              </div>
            )}

            {/* Client notes (e.g. dispute) — sourced from the parent dossier */}
            {(ordre.dossier_id || parentDossier?.id) && (
              <div className="mt-2">
                <ClientNotesBanner
                  dossierId={ordre.dossier_id || parentDossier?.id || ""}
                  notes={parentDossier?.client_notes || []}
                  onUpdated={(updated) => setParentDossier(updated)}
                />
              </div>
            )}

            {/* Payment status badges */}
            {payments.length > 0 && (
              <div className="flex flex-col gap-1.5 mt-2">
                {payments.filter((p) => p.state !== "cancelled").map((p) => {
                  const stateStyles: Record<string, { bg: string; color: string; label: string }> = {
                    draft: { bg: "#F3F4F6", color: "#6B7280", label: "Brouillon" },
                    initiated: { bg: "#DBEAFE", color: "#1E40AF", label: "En attente" },
                    pending: { bg: "#FEF3C7", color: "#92400E", label: "En cours" },
                    paid: { bg: "#D1FAE5", color: "#065F46", label: "Payé" },
                    error: { bg: "#FEE2E2", color: "#991B1B", label: "Erreur" },
                  };
                  const s = stateStyles[p.state] || stateStyles.draft;
                  const reason = p.state === "error" && p.bridge?.status_reason
                    ? ` — ${p.bridge.status_reason}`
                    : "";

                  return (
                    <div key={p.id} className="flex flex-row items-center gap-2">
                      <span
                        className="text-h4 font-medium rounded-full px-3 py-0.5"
                        style={{ background: s.bg, color: s.color }}
                      >
                        {p.type === "acompte" ? "Acompte" : "Solde"} {p.amount.toFixed(2)} € — {s.label}{reason}
                        {p.manual ? ` (${p.method ? PAYMENT_METHOD_LABELS[p.method] : "manuel"})` : ""}
                      </span>
                      {(p.state === "initiated" || p.state === "error") && (
                        <button
                          className="text-h4 font-medium hover:underline"
                          style={{ color: "#3B82F6" }}
                          onClick={async () => {
                            try {
                              await paymentsApi.resend(p.id);
                              showNotification({ type: "success", message: "Lien renvoyé" });
                            } catch {
                              showNotification({ type: "error", message: "Erreur lors du renvoi" });
                            }
                          }}
                        >
                          Renvoyer
                        </button>
                      )}
                      {p.state !== "paid" && p.state !== "cancelled" && (
                        <button
                          className="text-h4 font-medium hover:underline"
                          style={{ color: "#059669" }}
                          onClick={() => setManualPaymentModal({ mode: "mark", payment: p })}
                        >
                          Marquer payé
                        </button>
                      )}
                      {p.state !== "paid" && p.state !== "cancelled" && (
                        <button
                          className="text-h4 font-medium hover:underline"
                          style={{ color: "#EF4444" }}
                          onClick={async () => {
                            try {
                              await paymentsApi.cancel(p.id);
                              fetchPayments();
                              showNotification({ type: "success", message: "Paiement annulé" });
                            } catch {
                              showNotification({ type: "error", message: "Erreur lors de l'annulation" });
                            }
                          }}
                        >
                          Annuler
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Late deposit warning */}
            {(() => {
              const preReceptionStatuses: OrdreStatus[] = ["a_preparer", "commande", "pret"];
              if (!preReceptionStatuses.includes(ordre.status)) return null;
              if (!ordre.reception_date?.seconds) return null;
              const depositDate = new Date(ordre.reception_date.seconds * 1000);
              if (Date.now() <= depositDate.getTime()) return null;
              return (
                <div className="flex flex-row items-center gap-2 mt-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200">
                  <Icon1 name="warning" size={16} color="#D97706" />
                  <span className="text-h4 font-medium text-amber-700">
                    La date de depot est depassee et le vehicule n'a pas encore ete receptionne
                  </span>
                </div>
              );
            })()}

          </div>

          {/* Vehicle image + Actions */}
          <div className="flex flex-row items-start gap-4 flex-shrink-0">
            {primaryVehicle && (
              <div className="flex flex-col items-center gap-1">
                <div className="rounded-xl border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden" style={{ width: 160, height: 90 }}>
                  <VehicleImage marque={primaryVehicle.marque} modele={primaryVehicle.modele} coloris={primaryVehicle.coloris} mec={primaryVehicle.mec} alt={primaryVehicle.plate} className="w-full h-full" iconSize={32} />
                </div>
                <span className="text-h4 text-neutral-500 font-medium">{primaryVehicle.plate}</span>
              </div>
            )}

            <OrdreActionsMenu
              ordre={ordre}
              orgData={orgData}
              context="page"
              closeRef={actionsMenuCloseRef}
              payments={payments}
              rentalVehicle={rentalVehicle}
              hasFacture={hasFacture}
              serviceboxProxyAvailable={serviceboxProxyAvailable}
              sbEstimationLoading={sbEstimationLoading}
              onEdit={() => setShowEditModal(true)}
              onDelete={() => setShowDeleteConfirm(true)}
              onRequestFlagReason={(flag, newFlags) => { setFlagReasonText(""); setFlagReasonModal({ flag, newFlags }); }}
              onShowReminder={() => setShowReminderModal(true)}
              onAddDocument={() => setShowAddDoc(true)}
              onAddFacture={() => setShowFactureUpload(true)}
              onServiceBoxTransfer={() => setShowServiceBoxModal(true)}
              onImportEstimation={() => { handleImportServiceBoxEstimation(); }}
              onShowSecondary={() => setShowSecondaryModal(true)}
              onShowRental={() => setShowRentalModal(true)}
              onChangeRental={async () => {
                if (ordre.rental_reservation_id) { try { await vehiclesApi.deleteReservation(ordre.rental_reservation_id); } catch {} }
                const updatedOrdre = await ordresApi.update(ordre.id, { rental_vehicle_id: "", rental_reservation_id: "" });
                useOrdreStore.getState().updateOrdre(updatedOrdre);
                setRentalVehicle(null);
                setShowRentalModal(true);
              }}
              onRemoveRental={async () => {
                try {
                  if (ordre.rental_reservation_id) await vehiclesApi.deleteReservation(ordre.rental_reservation_id);
                  const updatedOrdre = await ordresApi.update(ordre.id, { rental_vehicle_id: "", rental_reservation_id: "" });
                  useOrdreStore.getState().updateOrdre(updatedOrdre);
                  setRentalVehicle(null);
                  showNotification({ type: "success", message: "Véhicule de remplacement retiré" });
                } catch { showNotification({ type: "error", message: "Erreur" }); }
              }}
              onEditReservation={() => setShowEditRentalRes(true)}
              onShowContrat={() => setShowContratModal(true)}
              onShowTracking={() => setShowTrackingPreview(true)}
              onShowPayment={(type) => setShowPaymentModal(type)}
              onRecordManualPayment={() => setManualPaymentModal({ mode: "create" })}
            />
          </div>
        </div>

        {/* Tabs — sticky so they stay visible when a tab's content (e.g. a long
            vehicle list) scrolls. z-20 + solid bg so scrolling content passes
            behind them instead of overlapping. */}
        <div className="px-6 pt-2 pb-2 sticky top-0 z-20 bg-white">
          <ChunkyTabs
            activeTab={activeTab}
            tabs={ordre?.has_unseen_client_docs && !docsSeen
              ? CONTENT_TABS.map((t) => (t.value === "documents" ? { ...t, badge: 1, badgeColor: "#6366f1" } : t))
              : CONTENT_TABS}
            setActiveTab={(t) => {
              setActiveTab(t);
              if (t === "documents" && ordre && ordre.has_unseen_client_docs && !docsSeen) {
                setDocsSeen(true);
                ordresApi.update(ordre.id, { has_unseen_client_docs: false }).catch(() => {});
              }
            }}
          />
        </div>

        {/* Search */}
        <div className="px-6 pt-3" style={{ maxWidth: 500 }}>
          <SearchField value={search} onChange={setSearch} placeholder="Chercher .." />
        </div>

        {/* Tab content */}
        <div className="px-6 pt-4 pb-8 flex-1">
          {activeTab === "fil" && <FilTab timeline={ordre.timeline || []} search={search} documents={ordre.documents} ordreId={ordre.id} onPreview={(doc, url) => setPreviewDoc({ doc, url })} orgMembers={orgMembers} />}
          {activeTab === "contacts" && <ContactsTab contacts={parentDossier?.contacts || ordre.contacts || []} dossierId={ordre.dossier_id || parentDossier?.id} ordreId={ordre.id} />}
          {activeTab === "interventions" && <InterventionsTab interventions={ordre.interventions} configuredInterventions={configuredInterventions} interventionGroups={ordre.intervention_groups} ordreId={ordre.id} atelierCharges={ordreAtelierCharges} onRefresh={() => refreshOrdre()} onCatalogUpdated={setConfiguredInterventions} />}
          {activeTab === "vehicules" && (
              <VehiculesTab
                locked={Boolean(ordre.servicebox_rdv_id)}
                dossierVehicles={parentDossier?.vehicles || (ordre.vehicle ? [ordre.vehicle] : [])}
                selectedVehicleId={ordre.vehicle_id || ordre.vehicle?.id}
                onSelectVehicle={async (vehicleId) => {
                  if (ordre.servicebox_rdv_id) return;
                  try {
                    const updated = await ordresApi.update(ordre.id, { vehicle_id: vehicleId } as any);
                    useOrdreStore.getState().updateOrdre({
                      ...updated,
                      vehicle: (parentDossier?.vehicles || []).find((v) => v.id === vehicleId),
                    });
                    showNotification({ type: "success", message: "Véhicule sélectionné" });
                  } catch {
                    showNotification({ type: "error", message: "Erreur lors de la sélection" });
                  }
                }}
                onSaveVehicle={async (vehicle, data) => {
                  if (!parentDossier) return;
                  try {
                    const updatedVehicles = (parentDossier.vehicles || []).map((v) =>
                      v.id === vehicle.id ? { ...v, ...data } : v
                    );
                    const updatedDossier = await dossiersApi.update(parentDossier.id, { vehicles: updatedVehicles } as any);
                    setParentDossier(updatedDossier);
                    try { await clientVehiclesApi.update(vehicle.id, data); } catch { /* shared client_vehicles copy, best-effort */ }
                    if (ordre.vehicle_id === vehicle.id || ordre.vehicle?.id === vehicle.id) {
                      const updated = updatedVehicles.find((v) => v.id === vehicle.id);
                      if (updated) useOrdreStore.getState().updateOrdre({ ...ordre, vehicle: updated as any });
                    }
                    showNotification({ type: "success", message: "Véhicule modifié" });
                  } catch {
                    showNotification({ type: "error", message: "Erreur lors de la sauvegarde" });
                  }
                }}
                parentDossier={parentDossier}
              />
            )}
          {activeTab === "documents" && (
              <DocumentsTab
                documents={(() => {
                  const dossierDocs = parentDossier?.documents || [];
                  const ordreDocs = ordre.documents || [];
                  const seen = new Set<string>();
                  const merged: typeof dossierDocs = [];
                  for (const d of [...ordreDocs, ...dossierDocs]) {
                    if (!seen.has(d.id)) { seen.add(d.id); merged.push(d); }
                  }
                  return merged;
                })()}
                dossierId={ordre.dossier_id}
                ordreId={ordre.id}
                onPreview={openDocPreview}
                onAddDoc={() => setShowAddDoc(true)}
                onDeleteDoc={(doc) => setDeleteDocConfirm(doc)}
                devisSignStatus={ordre.devis_sign_status}
                devisDocumentId={ordre.devis_document_id}
              />
            )}
        </div>
      </div>

      {/* Right chat panel — full on xl screens */}
      <div className="flex-shrink-0 hidden 2xl:block" style={{ width: 400 }}>
        <ChatPanel
          discussionId={parentDossier?.discussion_id || ordre.discussion_id}
          internalDiscussionId={parentDossier?.internal_discussion_id || ordre.internal_discussion_id}
          entityType="ordre"
          entityId={ordre.id}
          entityName={ordre.name}
          members={[{ name: "M. Martin" }, { name: "Emeric" }, { name: "Mélanie" }]}
          onlineCount={2}
          whatsappLinked={!!(orgData?.whatsapp?.connected && (parentDossier?.phone || ordre.contacts?.[0]?.phone))}
          whatsappProvider={orgData?.whatsapp?.provider || "wasender"}
        />
      </div>

      {/* Floating chat widget — below xl only */}
      <FloatingChatWidget
        discussionId={parentDossier?.discussion_id || ordre.discussion_id}
        internalDiscussionId={parentDossier?.internal_discussion_id || ordre.internal_discussion_id}
        entityType="ordre"
        entityId={ordre.id}
        entityName={ordre.name}
        whatsappLinked={!!(orgData?.whatsapp?.connected && (parentDossier?.phone || ordre.contacts?.[0]?.phone))}
        whatsappProvider={orgData?.whatsapp?.provider || "wasender"}
      />

      {/* Add document modal — uploads to parent dossier */}
      {showAddDoc && ordre.dossier_id && (
        <AddDocumentModalOrdre
          dossierId={ordre.dossier_id}
          onClose={() => setShowAddDoc(false)}
          onSuccess={(updated) => { setParentDossier(updated); setShowAddDoc(false); }}
        />
      )}

      {/* Facture upload modal — uploads to ordre documents */}
      {showFactureUpload && (
        <FactureUploadModal
          ordreId={ordre.id}
          onClose={() => setShowFactureUpload(false)}
          onSuccess={(updated) => {
            useOrdreStore.getState().updateOrdre(updated);
            setShowFactureUpload(false);
          }}
        />
      )}

      {/* Document preview modal */}
      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc.doc}
          url={previewDoc.url}
          onClose={() => setPreviewDoc(null)}
          onDownload={() => window.open(previewDoc.url, "_blank")}
        />
      )}

      {/* Tracking preview modal (phone frame) */}
      {showTrackingPreview && ordre && (
        <TrackingPreviewModal
          ordre={ordre}
          parentDossier={parentDossier}
          orgData={orgData}
          onClose={() => setShowTrackingPreview(false)}
        />
      )}
      {showRentalModal && ordre && orgData?.id && (
        <RentalVehicleModal
          ordre={ordre}
          orgId={orgData.id}
          onClose={() => setShowRentalModal(false)}
          onAssign={(v) => {
            setRentalVehicle(v);
            setShowContratModal(true);
          }}
        />
      )}
      {showEditRentalRes && ordre && (
        <EditRentalReservationModal
          ordre={ordre}
          onClose={() => setShowEditRentalRes(false)}
        />
      )}
      {showContratModal && ordre && rentalVehicle && orgData && (
        <ContratLocationModal
          ordre={ordre}
          rentalVehicle={rentalVehicle}
          contact={ordre.contacts?.find((c) => c.is_primary) || ordre.contacts?.[0] || parentDossier?.contacts?.find((c) => c.is_primary) || parentDossier?.contacts?.[0]}
          clientVehicle={primaryVehicle}
          orgData={orgData}
          onClose={() => setShowContratModal(false)}
        />
      )}
      {showServiceBoxEstimation && ordre && (
        <EstimateExtractionModal
          ordre={ordre}
          initialExtraction={sbExtraction || undefined}
          isExtracting={sbEstimationLoading}
          onClose={() => { setShowServiceBoxEstimation(false); setSbExtraction(null); }}
        />
      )}
      {sbForfaitChoices && sbForfaitChoices.length > 0 && (
        <BaseModal onClose={() => setSbForfaitChoices(null)} title="Choisir le forfait" startIcon={<Icon1 name="sett-servicebox" size={20} />} width="max-w-lg">
          <div className="flex flex-col gap-3 p-4">
            <span className="text-h3 text-neutral-500">ServiceBox propose plusieurs forfaits. Choisissez la variante à appliquer :</span>
            {Object.entries(sbForfaitChoices.reduce<Record<string, ForfaitChoice[]>>((acc, c) => { (acc[c.field] ||= []).push(c); return acc; }, {})).map(([field, opts]) => (
              <div key={field} className="flex flex-col gap-1.5">
                {opts.map((c) => {
                  const checked = sbForfaitSel[field] === c.code;
                  return (
                    <label key={c.code} className="flex flex-row items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer" style={{ border: checked ? "2px solid #000" : "1.5px solid #E5E5E5", background: checked ? "#F5F5F5" : "#fff" }}>
                      <input type="radio" name={field} checked={checked} onChange={() => setSbForfaitSel((p) => ({ ...p, [field]: c.code }))} />
                      <div className="flex flex-col flex-1">
                        <span className="text-h3 font-medium text-neutral-700">{c.code} — {c.designation}</span>
                        <span className="text-h4 text-neutral-400">{c.prix_ttc} € TTC{c.prix_ht ? ` · ${c.prix_ht} € HT` : ""}</span>
                      </div>
                    </label>
                  );
                })}
              </div>
            ))}
            <div className="flex flex-row gap-2 pt-1">
              <Button label={sbForfaitSubmitting ? "Application…" : "Valider et générer le devis"} onClick={submitForfaitChoice} disabled={sbForfaitSubmitting} variant="small" />
              <SecondaryButton label="Annuler" onClick={() => setSbForfaitChoices(null)} variant="small" />
            </div>
          </div>
        </BaseModal>
      )}
      {showPaymentModal && ordre && (
        <PaymentRequestModal
          ordre={ordre}
          defaultType={showPaymentModal}
          onClose={() => setShowPaymentModal(null)}
          onSuccess={() => {
            fetchPayments();
            ordresApi.get(ordre.id).then((updated) => {
              useOrdreStore.getState().updateOrdre(updated);
            }).catch(() => {});
            // Refresh chat messages to show payment link message
            const discId = parentDossier?.discussion_id || ordre.discussion_id;
            if (discId) {
              useMessagerieStore.getState().fetchMessages(discId);
            }
          }}
        />
      )}
      {manualPaymentModal && ordre && (
        <ManualPaymentModal
          ordre={ordre}
          mode={manualPaymentModal.mode}
          payment={manualPaymentModal.payment}
          defaultType={manualPaymentModal.defaultType}
          onClose={() => setManualPaymentModal(null)}
          onSuccess={() => {
            fetchPayments();
            ordresApi.get(ordre.id).then((updated) => {
              useOrdreStore.getState().updateOrdre(updated);
            }).catch(() => {});
          }}
        />
      )}
      {showSecondaryModal && ordre && (
        <SecondaryOrdreModal
          ordre={ordre}
          onClose={() => setShowSecondaryModal(false)}
        />
      )}
      {showServiceBoxModal && ordre && orgData?.servicebox && (
        <ServiceBoxTransferModal
          ordre={ordre}
          serviceboxConfig={orgData.servicebox}
          onClose={() => setShowServiceBoxModal(false)}
        />
      )}
      {showReminderModal && ordre?.intervention_groups && (() => {
        const ig = ordre.intervention_groups;
        const activeToken = Object.values(ig).map((g) => g.devis_token).filter(Boolean).pop();
        return activeToken ? (
          <DevisReminderModal
            onClose={() => setShowReminderModal(false)}
            tokenId={activeToken}
          />
        ) : null;
      })()}
      {showProxyToast && (
        <div className="fixed top-6 right-6 z-[200] min-w-280 bg-white border border-neutral-200 rounded-xl shadow-lg overflow-hidden animate-[slideInRight_0.3s_ease-out]">
          <div className="flex flex-row items-center gap-3 px-4 py-3">
            <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
              <Icon1 name="done2" size={18} color="#3B82F6" />
            </div>
            <div className="flex-1">
              <p className="text-h3 font-medium text-neutral-800">ServiceBox connecté</p>
              <p className="text-h4 text-neutral-500">Proxy disponible</p>
            </div>
          </div>
          <div className="h-1 bg-neutral-100">
            <div
              className="h-full bg-blue-500 rounded-r"
              style={{ animation: "proxyBarFill 3s linear forwards" }}
            />
          </div>
          <style>{`
            @keyframes proxyBarFill { from { width: 0%; } to { width: 100%; } }
            @keyframes slideInRight { from { opacity: 0; transform: translateX(100%); } to { opacity: 1; transform: translateX(0); } }
          `}</style>
        </div>
      )}
    </div>
  );
}
